import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Accordion,
  Carousel,
  Modal,
} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Image from "react-bootstrap/Image";
import { motion } from "motion/react";
import { Clock, DollarSign, Settings, ArrowLeftRight, CheckCircle2, Cloud, Database, Rocket, Building2, Zap, Users, Target, Shield, TrendingUp, Globe, Quote } from "lucide-react";
import { Brain, Repeat, ShieldCheck, Factory, HeartPulse, Shirt, UserCheck, BarChart, Link as LinkIcon } from "lucide-react";
import { Helmet } from "react-helmet";
import { RotateCcw } from "lucide-react";
import enquirySuccess from "../assets/images/enquiry-success.png";
import "../assets/css/contact.css";
import LayerFour from "../assets/images/layers/Layer_4.png";
import "../assets/css/AIFirstDeliveryCenter.css";
// Decorative banner shapes to match existing site look
import bannerShape1 from "../assets/images/layers/banner_layer_one.png";
import bannerShape2 from "../assets/images/layers/banner_layer_two.png";
import flutterHero from "../assets/images/banner/gcc.png";







import { apiUrl, apiHeaders } from "../apiConfig";

const DataReadinessModalContent = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    mobile: "",
    company: "",
    jobTitle: "",
    message: "",
    role: "",
    subscribe: "yes",
    heardAbout: "",
    otherChallenge: "",
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [errors, setErrors] = useState({});

  const businessEmailRegex =
    /@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|aol\.com|icloud\.com|protonmail\.com|zoho\.com|mail\.com|gmx\.com)$/i;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "First name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (businessEmailRegex.test(formData.email)) {
      newErrors.email = "Please use a business email address";
    }

    if (!formData.mobile) {
      newErrors.mobile = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit phone number";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = "Job title is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage("");
    setErrors({});

    if (!validateForm()) {
      setLoading(false);
      setResponseMessage("Please correct the errors in the form.");
      return;
    }

    try {
      // The server builds and sends the emails; SMTP credentials stay server-side
      const res = await fetch(apiUrl("/api/gcc-blueprint"), {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          company: formData.company,
          jobTitle: formData.jobTitle,
        }),
      });
      const response = await res.json().catch(() => null);
      if (!response) {
        // Non-JSON reply: the API server is down or restarting
        setResponseMessage("We couldn’t reach our server. Please try again in a moment.");
      } else if (res.ok && response.success) {
        setIsSubmitted(true);
        setFormData((prev) => ({
          ...prev,
          name: "",
          email: "",
          mobile: "",
          company: "",
          jobTitle: "",
        }));
      } else {
        setResponseMessage(
          response.message || "Failed to send email. Please try again."
        );
      }
    } catch (error) {
      setResponseMessage("We couldn’t reach our server. Please try again in a moment.");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="bg-E2F1FE position-relative box-shadow-one rounded-4 pt-5 p-23 p-sm-23">
        <Image
          src={LayerFour}
          className="position-absolute bottom-0 start-0 w-40"
        />

        <Row className="position-relative form-box z-0">
          {isSubmitted ? null : (
            <Col lg={5}>
              <div className="text-left d-flex justify-content-start">
                <h2 className="section-heading fw-700 fs-2">
                  Get Your{" "}
                  <span className="bordered-text bordered-text-start">
                    GCC{" "}
                  </span>
                  <span className="text-ee0000"> Blueprint</span>
                </h2>
              </div>
            </Col>
          )}
          <Col lg={isSubmitted ? 12 : 7}>
            {isSubmitted ? (
              <div
                className="enquiry-success enquiry-success--row"
                role="status"
                aria-live="polite"
              >
                <Image src={enquirySuccess} alt="" className="enquiry-success-img" />
                <div className="enquiry-success-body">
                  <h3 className="enquiry-success-title">Thank you</h3>
                  <p className="enquiry-success-subtitle">
                    We&rsquo;ve received your GCC Blueprint request.
                  </p>
                  <p className="enquiry-success-text">
                    Our GCC experts will review your details and get in touch
                    with you soon. A confirmation has been sent to your inbox.
                  </p>
                  <div className="enquiry-success-actions">
                    <button
                      type="button"
                      className="enquiry-success-link"
                      onClick={() => setIsSubmitted(false)}
                    >
                      <RotateCcw size={15} /> Submit another request
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Row className="pb-3 mt-3 mt-lg-0">
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Name <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.name && (
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  {/* <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        isInvalid={!!errors.lastName}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.lastName && (
                        <Form.Control.Feedback type="invalid">
                          {errors.lastName}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col> */}
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Company <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        isInvalid={!!errors.company}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.company && (
                        <Form.Control.Feedback type="invalid">
                          {errors.company}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Email <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder=""
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.email && (
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>Job Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        isInvalid={!!errors.jobTitle}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.jobTitle && (
                        <Form.Control.Feedback type="invalid">
                          {errors.jobTitle}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="mobile"
                        placeholder="Enter 10-digit phone number"
                        value={formData.mobile}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^\d]/g, "")
                            .slice(0, 10);
                          setFormData({ ...formData, mobile: value });
                        }}
                        isInvalid={!!errors.mobile}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.mobile && (
                        <Form.Control.Feedback type="invalid">
                          {errors.mobile}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  {/* <Col lg={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>How did you hear about us?</Form.Label>
                      <Form.Select
                        name="heardAbout"
                        value={formData.heardAbout}
                        onChange={handleChange}
                        className="rounded-1 custom-input custom-input-two"
                        isInvalid={!!errors.heardAbout}
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="Social Media">Social Media</option>
                        <option value="Friend">Friend</option>
                        <option value="Search Engine">Search Engine</option>
                        <option value="Advertisement">Advertisement</option>
                        <option value="Event">Event</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      {errors.heardAbout && (
                        <Form.Control.Feedback type="invalid">
                          {errors.heardAbout}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>What is your role?</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role || ""}
                        onChange={handleChange}
                        className="rounded-1 custom-input custom-input-two"
                        isInvalid={!!errors.role}
                      >
                        <option value="">Select</option>
                        <option value="Developer">Developer</option>
                        <option value="Manager">Manager</option>
                        <option value="Designer">Designer</option>
                        <option value="Marketer">Marketer</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      {errors.role && (
                        <Form.Control.Feedback type="invalid">
                          {errors.role}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        How can we help your organization?
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder=""
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        isInvalid={!!errors.message}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.message && (
                        <Form.Control.Feedback type="invalid">
                          {errors.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col> */}
                  {/* <Col lg={12}>
                    <Form.Group className="mb-3 mt-1">
                      <Form.Label className="mb-3">
                        I would like to receive emails from{" "}
                        <span className="fw-700">Comsense</span> with exclusive
                        updates on products, services, events, and alerts. I may
                        unsubscribe at any time.
                      </Form.Label>
                      <div>
                        <Form.Check
                          type="radio"
                          label="Yes"
                          name="subscribe"
                          value="yes"
                          className="d-block"
                          checked={formData.subscribe === "yes"}
                          onChange={handleChange}
                          inline
                        />
                        <Form.Check
                          type="radio"
                          label="No"
                          name="subscribe"
                          value="no"
                          className="d-block"
                          checked={formData.subscribe === "no"}
                          onChange={handleChange}
                          inline
                        />
                      </div>
                    </Form.Group>
                  </Col> */}

                  <Col lg={12} className="mt-4 text-start">
                    <>
                      <Button
                        variant=""
                        type="submit"
                        className="fw-600 d-block btn btn-danger btn-xl"
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Submit"}
                      </Button>
                    </>
                  </Col>
                  {responseMessage && (
                    <p
                      className="mt-3 mb-0 fs-12 text-center"
                      style={{ color: loading ? "blue" : "red" }}
                    >
                      {responseMessage}
                    </p>
                  )}
                </Row>
              </Form>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

const GlobalCapabilityCenter = () => {
  const [activeKey, setActiveKey] = useState("0");
  const [showDataReadinessModal, setShowDataReadinessModal] = useState(false);

  const openDataReadinessModal = () => setShowDataReadinessModal(true);
  const closeDataReadinessModal = () => setShowDataReadinessModal(false);

  return (
    <>
      <Helmet>
        <title>
          Global Capability Center Pune, India | AI, Data & GCC Setup
        </title>

        <meta
          name="description"
          content="Build AI, Data & Salesforce GCC in India with zero CapEx. Set up offshore teams faster with scalable, cost-efficient global capability centers."
        />

        <meta
          name="keywords"
          content="global capability center India, GCC setup India, offshore development center India, AI data GCC solutions"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Comsense Technologies" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Global Capability Center Pune, India | AI, Data & GCC Setup Services"
        />
        <meta
          property="og:description"
          content="Set up scalable offshore GCC teams in India with AI, data & Salesforce expertise."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://comsensetechnologies.com/global-capability-center/GCC"
        />
      </Helmet>
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');              .gcc-outfit { font-family: 'Outfit', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'; }
  
.gcc-outfit  *{ font-family: "Outfit", sans-serif;}

              @keyframes floatY { 0% { transform: translateY(0); } 50% { transform: translateY(-8px); } 100% { transform: translateY(0); } }
              @keyframes floatX { 0% { transform: translateX(0); } 50% { transform: translateX(8px); } 100% { transform: translateX(0); } }
              @keyframes pulseGlow { 0% { box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
                                     50% { box-shadow: 0 12px 28px rgba(0,0,0,0.12); }
                                     100% { box-shadow: 0 8px 24px rgba(0,0,0,0.08); } }
              .shape-float-y { animation: floatY 12s ease-in-out infinite; }
              .shape-float-x { animation: floatX 14s ease-in-out infinite; }
              .card-modern { border-radius: 12px; background: #fff; box-shadow: 0 8px 24px rgba(0,0,0,0.08); transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease; border: 1px solid transparent; }
              .card-modern:hover { transform: translateY(-4px); animation: pulseGlow 1.6s ease-in-out; border-color: #067bb1; box-shadow: 0 12px 32px rgba(6,123,177,0.15); }
              .card-icon { width:40px; height:40px; border-radius:50%; display:flex; align-items:center; justify-content:center; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); border:1px solid #067bb1; color: #fff; }
              .card-accent-red { border-top:4px solid #067bb1; }
              .card-accent-blue { border-top:4px solid #2563eb; }
              .card-accent-violet { border-top:4px solid #7c3aed; }
              .badge-pill { display:inline-block; padding:6px 12px; border-radius:999px; font-size:12px; font-weight:600; }
              .badge-prime { background:#E2F1FE; color:#067bb1; border:1px solid #067bb1; }
              .badge-partner { background:#eef6ff; color:#2563eb; border:1px solid #2563eb; }
              .stat-chip { display:inline-flex; align-items:center; gap:6px; padding:8px 12px; border-radius:999px; background:#f7fbff; border:1px solid #e6f0ff; font-weight:600; font-size:12px; }
              .card-title-lg { font-size: 1.4rem; letter-spacing: -0.02rem; }
              .card-subtitle-sm { font-size: 12px; color: #64748b; font-weight: 600; }
              /* Hero Enhancements */
              .hero-tag { display:inline-flex; align-items:center; gap:8px; padding:6px 12px; border-radius:999px; background:#E2F1FE; color:#028bc6; border:1px solid #b8daff; font-weight:700; font-size:12px; }
              .hero-title { font-weight:800; font-size:42px; line-height:1.1; letter-spacing:-0.02rem; }
              .highlight-red { color:#028bc6;  }
              .gcc-highlight-pill { display:inline-block; padding: 10px 18px; border-radius: 999px; background: rgba(6,123,177,0.10); border: 1px solid rgba(6,123,177,0.28); color: #04467a; box-shadow: 0 12px 28px rgba(6,123,177,0.14); }
              .gcc-highlight-marker { color: #04467a; padding: 0 6px; background: linear-gradient(transparent 62%, rgba(6,123,177,0.18) 62%); border-radius: 8px; }
              .hero-chip-row { display:flex; gap:12px; flex-wrap:wrap; margin:18px 0; }
              .hero-media { border-radius:16px; overflow:hidden; 
            //   box-shadow:0 20px 60px rgba(16,24,40,0.12);
               }
              .btn-red { text-decoration:none;background: #e00; color:#fff; border:none; border-radius:12px; padding:10px 18px; font-weight:700; box-shadow:0 12px 24px rgba(238,0,0,0.18);padding:10px 18px; }
              .btn-outline-dark { text-decoration:none;background:#fff; color:#0f172a; border:1px solid #e5e7eb; border-radius:12px; padding:10px 18px; font-weight:700; padding:10px 18px; }
              /* Hero Stat Cards (icon chips) */
              .stat-card { display:flex; align-items:center; gap:12px; }
              .stat-icon { width:36px; height:36px; border-radius:12px; display:flex; align-items:center; justify-content:center; border:1px solid transparent; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); color: #fff; }
              .stat-icon-green { background: #f0fff5;
  border-color: #d7fbe7;
  color: #10b981; }
              .stat-icon-orange {   background: #fff7ed;
  border-color: #fde2c5;
  color: #f59e0b; }
              .stat-icon-pink { background: #fdf2f6;
  border-color: #f8d7e5;
  color: #ef4444; }
              .stat-label { font-size:12px; color:#64748b; }
              .stat-value { font-size:16px; font-weight:800; color:#0f172a; }
              /* Pods card styling inspired by reference card */
              .pod-card { position: relative; overflow: hidden; border: 1px solid #e6f0ff; }
              .pod-shadow-red { box-shadow: 0 6px 0 rgba(238,0,0,0.15); }
              .pod-shadow-blue { box-shadow: 0 6px 0 rgba(37,99,235,0.15); }
              .pod-shadow-violet { box-shadow: 0 6px 0 rgba(124,58,237,0.15); }
              .pod-title { font-size:1.25rem; letter-spacing:-0.02rem; color:#0f172a; }
              .pod-list { font-size:13px; color:#334155; }
              .pod-accent-shape { position:absolute; bottom:10px; right:10px; width:68px; height:68px; border-radius:999px; background: radial-gradient(circle at 40% 40%, rgba(37,99,235,0.14) 0%, rgba(37,99,235,0.08) 45%, transparent 70%); }
              
              /* The Problem Section */
              .problem-section { padding: 70px 0 70px 0; background: #fff; position: relative; overflow: hidden; }
              .problem-pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 999px; background: #E2F1FE; color: #067bb1; border: 1px solid #b8daff; font-size: 12px; font-weight: 700; margin-bottom: 24px; }
              .problem-title { font-size: 42px; font-weight: 800; color: #000; letter-spacing: -0.02em; margin-bottom: 24px; }
              .problem-desc { font-size: 18px; color: #475569; max-width: 700px; margin: 0 auto 60px; line-height: 1.6; }
              .problem-card { box-shadow: 0 2px 0 rgba(0,0,0,0.04);position: relative; background: #fff; border-radius: 24px; padding: 32px 24px; border: 1px solid #e2e8f0; height: 100%; overflow: hidden; z-index: 1; transition: all 0.3s ease; }
              .problem-card:hover { border-color: #067bb1; box-shadow: 0 8px 32px rgba(6,123,177,0.15); }
              .problem-icon-box { width: 64px; height: 64px; border-radius: 18px; display: flex; align-items: center; justify-content: center; margin-bottom: 24px; position: relative; z-index: 2; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); color: #fff; box-shadow: 0 8px 20px rgba(6,123,177,0.25); }
              .problem-icon-box svg { transition: transform 0.3s ease; }
              .problem-card:hover .problem-icon-box svg, .outcome-card:hover .outcome-icon-box svg { transform: scale(1.1) rotate(0deg); }
              .problem-card-title { font-size: 20px; font-weight: 700; color: #0f172a; margin-bottom: 12px; transition: color 0.3s ease; }
              .problem-card:hover .problem-card-title, .outcome-card:hover .fs-5.fw-bold { color: #067bb1; }
              .problem-card-text { font-size: 14px; color: #64748b; line-height: 1.5; }
              
              /* Updated Top-Right Shape to match screenshot design */
              .shape-tr-circle { 
                  position: absolute; 
                  top: -60px; 
                  right: -60px; 
                  width: 150px; 
                  height: 150px; 
                  
                  border-radius: 50%; 
                  background: transparent;
                  border: 1px solid rgba(6,123,177,0.15);
                  z-index: 0;
                  pointer-events: none;
              }
              .shape-tr-inner {
                  position: absolute;
                  top: -30px;
                  right: -30px;
                  width: 120px;
                  height: 120px;
                  border-radius: 50%;
                  background: radial-gradient(circle at center, rgba(6,123,177,0.04) 0%, transparent 70%);
                  z-index: 0;
              }

              .shape-bl-circle { position: absolute; bottom: 0; left: 0; width: 100px; height: 100px; background: radial-gradient(circle at bottom left, rgba(6,123,177,0.1), transparent 70%); border-top-right-radius: 100%; opacity: 0.6; }
              .shape-diamond { position: absolute; top: 20px; right: 20px; width: 12px; height: 12px; background: #b8daff; transform: rotate(45deg); opacity: 0; transition: all 0.4s ease; }
              .problem-card:hover .shape-diamond, .outcome-card:hover .shape-diamond { opacity: 1; transform: rotate(45deg) translate(10px, -10px); }
              .icon-glow { position: absolute; width: 100%; height: 100%; border-radius: 16px; filter: blur(12px); opacity: 0; transition: opacity 0.3s; z-index: -1; background: rgba(6,123,177,0.4); }
              .problem-card:hover .icon-glow, .outcome-card:hover .icon-glow { opacity: 1; }
              .mini-badge { position: absolute; bottom: -4px; right: -4px; width: 16px; height: 16px; background: #fff; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transform: scale(0); transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
              .problem-card:hover .mini-badge, .outcome-card:hover .mini-badge { transform: scale(1); }
              .line-top { position: absolute; top: 0; left: 0; width: 100%; height: 2px; background: linear-gradient(90deg, #067bb1, transparent); opacity: 0; transition: opacity 0.3s; }
              .problem-card:hover .line-top, .outcome-card:hover .line-top { opacity: 1; }
              .line-bottom { position: absolute; bottom: 0; left: 0; width: 100%; height: 2px; background: linear-gradient(90deg, transparent, #067bb1); transform: scaleX(0); transform-origin: left; transition: transform 0.4s ease; }
              .problem-card:hover .line-bottom, .outcome-card:hover .line-bottom { transform: scaleX(1); }
              @keyframes rippleEffect { 0% { transform: scale(0.8); opacity: 0.5; } 100% { transform: scale(2.5); opacity: 0; } }
              .circle-border-anim { position: absolute; top: 10px; right: 10px; width: 40px; height: 40px; border: 1px solid rgba(6,123,177,0.3); border-radius: 50%; opacity: 0; pointer-events: none; }
              .problem-card:hover .circle-border-anim, .outcome-card:hover .circle-border-anim { animation: rippleEffect 0.8s ease-out; }
              
              /* Zero-CapEx GCC Model Section - Advanced Styling */
              .model-section { padding: 70px 0 70px 0; background: #fff; position: relative; overflow: hidden; }
              .approach-model-section{background: rgb(226, 241, 254) !important;}
              /* Header Styling */
              .model-pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 999px; background: #E2F1FE; color: #067bb1; border: 1px solid #b8daff; font-size: 12px; font-weight: 700; margin-bottom: 24px; }
              .model-title { font-size: 42px; font-weight: 800; color: #000; letter-spacing: -0.02em; margin-bottom: 24px; }
              .model-subtitle { font-size: 18px; color: #475569; max-width: 700px; margin: 0 auto 16px; font-weight: 500; }
              .model-cta-text { font-size: 16px; color: #067bb1; font-weight: 700; margin-bottom: 60px; }
              
              /* Card Container */
              .model-card { 
                position: relative; 
                background: #fff; 
                border-radius: 24px; 
                padding: 40px 32px; 
                border: 1px solid #e2e8f0; 
                height: 100%; 
                overflow: hidden; 
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); /* shadow-lg */
                transition: all 0.3s ease;
                z-index: 1;
              }
              
              /* Card Hover State */
              .model-card:hover { 
                border-color: rgba(6,123,177,0.4); /* border-[#067bb1]/40 */
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); /* shadow-2xl */
              }
              
              /* Top-Right Gradient Corner */
              .model-shape-tr { 
                position: absolute; 
                top: 0; 
                right: 0; 
                width: 120px; 
                height: 120px; 
                background: radial-gradient(circle at top right, rgba(6,123,177,0.15), transparent 70%); 
                border-bottom-left-radius: 100%; /* rounded-bl-full approx */
                z-index: 0;
                opacity: 0.6;
              }
              
              /* Expanding Circle Border (Ripple) */
              .model-ripple-tr {
                position: absolute;
                top: -20px;
                right: -20px;
                width: 140px;
                height: 140px;
                border: 1px solid rgba(6,123,177,0.2);
                border-radius: 50%;
                z-index: 0;
                transform: scale(1);
                opacity: 1;
                transition: transform 0.7s ease, opacity 0.7s ease;
              }
              .model-card:hover .model-ripple-tr {
                transform: scale(1.5);
                opacity: 0;
              }

              /* Bottom-Left Corner Gradient */
              .model-shape-bl { 
                position: absolute; 
                bottom: 0; 
                left: 0; 
                width: 80px; 
                height: 80px; 
                background: radial-gradient(circle at bottom left, rgba(6,123,177,0.1), transparent 70%); 
                border-top-right-radius: 100%; 
                z-index: 0;
              }
              
              /* Top Accent Line */
              .model-line-top {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 3px;
                background: linear-gradient(90deg, #067bb1, #6bb8e0);
                opacity: 0;
                transition: opacity 0.3s ease;
                z-index: 2;
              }
              .model-card:hover .model-line-top {
                opacity: 1;
              }
              
              /* Bottom Shine Effect */
              .model-shine-bottom {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 24px;
                background: linear-gradient(to top, rgba(6,123,177,0.05), transparent);
                opacity: 0;
                transition: opacity 0.3s ease;
                pointer-events: none;
                z-index: 1;
              }
              .model-card:hover .model-shine-bottom {
                opacity: 1;
              }

              /* Icon Box & Glow */
              .model-icon-box { 
                width: 64px; 
                height: 64px; 
                border-radius: 16px; 
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                margin-bottom: 24px; 
                color: white; 
                position: relative; 
                z-index: 2; 
                transition: transform 0.5s ease, background-color 0.3s ease;
             flex-shrink: 0;
                }
              .model-card:hover .model-icon-box {
                transform: scale(1.1) rotate(6deg);
              }
              
              /* Icon Glow Halo */
              .model-icon-glow {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background: rgba(6,123,177,0.6);
                filter: blur(15px);
                opacity: 0.4;
                transition: opacity 0.3s ease;
                z-index: -1;
              }
              .model-card:hover .model-icon-glow {
                opacity: 0.6;
              }

              /* Corner Dots on Icon */
              .model-icon-dot {
                position: absolute;
                width: 4px;
                height: 4px;
                background: white;
                border-radius: 50%;
                opacity: 0;
                transition: opacity 0.3s ease;
              }
              .model-icon-dot.top-left { top: 6px; left: 6px; transition-delay: 0s; }
              .model-icon-dot.bottom-right { bottom: 6px; right: 6px; transition-delay: 0.1s; }
              
              .model-card:hover .model-icon-dot { opacity: 1; }
              
              /* Floating Diamond */
              .model-icon-diamond {
                position: absolute;
                top: -6px;
                right: -6px;
                width: 8px;
                height: 8px;
                background: #6bb8e0;
                transform: rotate(45deg);
                opacity: 0;
                transition: all 0.5s ease;
              }
              .model-card:hover .model-icon-diamond {
                opacity: 1;
                transform: rotate(45deg) translate(2px, -2px);
              }

              /* Content Styling */
              .model-card-title { 
                font-size: 28px; 
                font-weight: 800; 
                color: #0f172a; 
                margin-bottom: 12px; 
                transition: color 0.3s ease; 
              }
              .model-card:hover .model-card-title {
                color: #067bb1;
              }
              /* Header row: icon left, title + time right */
              .model-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
              .model-header .model-icon-box { margin-bottom: 0; }
              .model-header-text { display: flex; flex-direction: column; }
              
              /* Timeline Badge */
              .model-time-pill { 
                display: inline-block; 
                padding: 6px 16px; 
                background: #E2F1FE; 
                color: #028bc6; 
                border-radius: 999px; 
                font-size: 13px; 
                font-weight: 800; 
                margin-bottom: 0; 
              }
              
              /* List Items */
              .model-list-item { 
                display: flex; 
                align-items: center; 
                gap: 12px; 
                margin-bottom: 5px; 
                font-size: 15px; 
                color: #475569; 
                font-weight: 500;
                transition: color 0.3s ease;
              }
              .model-list-item:hover {
                color: #0f172a; /* gray-900 */
              }
              
              .model-check-icon { 
                width: 24px; 
                height: 24px; 
                background: #E2F1FE; 
                border-radius: 8px; 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                color: #067bb1; 
                flex-shrink: 0;
                transition: background-color 0.3s ease;
              }
              .model-list-item:hover .model-check-icon {
                background: rgba(226, 241, 254, 0.8); /* slightly darker/more opacity */
              }

              /* Arrow Connector */
              .arrow-connector-container {
                display: none;
              }
              @media (min-width: 992px) {
                .arrow-connector-container {
                  display: block;
                  position: absolute;
                  top: 50%;
                  right: -30px;
                  transform: translateY(-50%);
                  width: 60px;
                  height: 20px;
                  z-index: 10;
                  pointer-events: none;
                }
                .arrow-line {
                  width: 100%;
                  height: 2px;
                  background: linear-gradient(90deg, #067bb1, #6bb8e0);
                  position: relative;
                  top: 9px;
                }
                .arrow-head {
                  position: absolute;
                  right: 0;
                  top: 50%;
                  transform: translateY(-50%);
                  width: 0;
                  height: 0;
                  border-top: 6px solid transparent;
                  border-bottom: 6px solid transparent;
                  border-left: 10px solid #6bb8e0;
                }
              }
              
              /* Background Orbs */
              .bg-orb {
                position: absolute;
                border-radius: 50%;
                filter: blur(40px);
                z-index: 0;
                opacity: 0.4;
                animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
              }
              .bg-orb-1 { top: 10%; left: 5%; width: 150px; height: 150px; background: #fee2e2; }
              .bg-orb-2 { bottom: 10%; right: 5%; width: 200px; height: 200px; background: #ffedd5; animation-delay: 1.5s; }
              
              @keyframes pulse {
                0%, 100% { opacity: 0.4; transform: scale(1); }
                50% { opacity: 0.6; transform: scale(1.1); }
              }

              /* High-Value GCC Pods Section - Advanced Effects */
              .gcc-pods-section {
                padding: 80px 0 70px 0;
                background: #effdff;
                position: relative;
                overflow: hidden;
              }
              
              /* 1. Grid Pattern */
              .gcc-pods-grid-bg {
                position: absolute;
                inset: 0;
                background-image: 
                  linear-gradient(to right, rgba(128, 128, 128, 0.12) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(128, 128, 128, 0.12) 1px, transparent 1px);
                background-size: 24px 24px;
                mask-image: radial-gradient(ellipse at center, black 40%, transparent 80%);
                z-index: 0;
                pointer-events: none;
                display: none;
              }

              /* 2. Animated Pulsing Orbs */
              .pod-orb {
                position: absolute;
                border-radius: 50%;
                filter: blur(120px);
                z-index: 0;
                animation: podPulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
              }
              .pod-orb-tl {
                top: -100px;
                left: -100px;
                width: 384px;
                height: 384px;
                // background: rgba(0, 126, 238, 0.2);
              }
              .pod-orb-br {
                bottom: -100px;
                right: -100px;
                width: 384px;
                height: 384px;
                // background: rgba(238, 0, 0, 0.1);
                animation-delay: 2s;
              }
              @keyframes podPulse {
                0%, 100% { opacity: 1; }
                50% { opacity: .5; }
              }

              /* 3. Badge with Pulsing Dot */
              .pod-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 6px 16px;
                border-radius: 9999px;
                background: rgba(255, 255, 255, 0.03);
                border: 1px solid rgba(6,123,177,0.2);
                backdrop-filter: blur(4px);
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
                text-transform: uppercase;
              }
              .pod-badge-dot {
                position: relative;
                width: 6px;
                height: 6px;
                background-color: #067bb1;
                border-radius: 50%;
              }
              .pod-badge-dot::after {
                content: '';
                position: absolute;
                inset: -4px;
                border-radius: 50%;
                background-color: #067bb1;
                opacity: 0.75;
                animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
              }
              @keyframes ping {
                75%, 100% { transform: scale(2); opacity: 0; }
              }

              .gcc-pods-title {
                color: #000;
                font-size: 42px; /* 6xl approx */
                font-weight: 800;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
                line-height: 1.1;
              }
              .gcc-pods-subtitle {
                font-size: 18px;
                max-width: 600px;
                margin: 0 auto 60px;
                line-height: 1.625;
              }

              /* 6. Card Background & Design */
              .pod-dark-card {
                position: relative;
                height: 100%;
                background: linear-gradient(135deg, #067bb1 0%, #04467a 100%); /* zinc-900 to black */
                border: 1px solid #067bb1 ; /* zinc-800 */
                border-radius: 24px; /* 3xl */
                padding: 40px 32px;
                overflow: hidden;
                transition: border-color 0.5s ease, transform 0.5s ease;
                z-index: 1;
              }
              .pod-dark-card:hover {
                border-color: #067bb1 ;
                transform: translateY(-5px);
              }

              /* 5. Outer Glow Effect */
              .pod-card-glow {
                position: absolute;
                inset: -1px;
                // background: linear-gradient(to bottom right, #ee0000, #7f1d1d);
                opacity: 0;
                filter: blur(24px); /* 2xl */
                transition: opacity 0.7s ease;
                z-index: -1;
                border-radius: 24px;
              }
              .pod-dark-card:hover .pod-card-glow {
                opacity: 0.2;
              }

              /* 7. Full-Card Shine Overlay */
              .pod-shine-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 100%);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
              }
              .pod-dark-card:hover .pod-shine-overlay {
                opacity: 1;
              }

              /* 8. Top Accent Line */
              .pod-line-top {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 1px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
                box-shadow: 0 0 4px #067bb1;
              }
              .pod-dark-card:hover .pod-line-top {
                opacity: 1;
              }

              /* 9. Icon Container & Glow */
              .pod-icon-wrapper {
                position: relative;
                width: 64px;
                height: 64px;
                border-radius: 16px; /* slightly rounded square */
                background: linear-gradient(135deg, #067bb1, #04467a);
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 32px;
                box-shadow: 0 10px 15px -3px rgba(6,123,177,0.3);
                transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                z-index: 2;
              }
              .pod-dark-card:hover .pod-icon-wrapper {
                transform: scale(1.1);
              }
              .pod-icon-glow {
                position: absolute;
                inset: 0;
                background: #067bb1;
                filter: blur(24px); /* xl blur */
                opacity: 0.5;
                transition: opacity 0.5s ease;
                z-index: -1;
                border-radius: 50%;
              }
              .pod-dark-card:hover .pod-icon-glow {
                opacity: 1;
              }
              .pod-icon-wrapper svg {
                color: #fff;
                z-index: 3;
              }

              /* 10. Decorative Dots */
              .pod-card-dots {
                position: absolute;
                top: 24px;
                right: 24px;
                display: flex;
                gap: 2px; /* 1.5px approx */
              }
              .pod-dot {
                width: 3px;
                height: 3px;
                background-color: #3f3f46; /* zinc-700 */
                border-radius: 50%;
              }
              .pod-dot.accent {
                background-color: #067bb1;
              }

              /* 11. Title Color Shift */
              .pod-card-title {
                color: #fff;
                font-size: 24px;
                font-weight: 700;
                margin-bottom: 24px;
                transition: color 0.3s ease;
              }
              .pod-dark-card:hover .pod-card-title {
                // color: #ee0000;
              }

              /* 12. Capability List Items */
              .pod-feature-list {
                list-style: none;
                padding: 0;
                margin: 0 0 40px 0;
              }
              .pod-feature-item {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 16px;
                color: #fff; /* gray-400 */
                font-size: 14px;
                line-height: 1.6;
                transition: color 0.3s ease;
                cursor: default;
              }
              .pod-feature-item:hover {
                color: #fff;
              }
              .pod-bullet {
                width: 4px;
                height: 4px;
                background-color: #067bb1;
                border-radius: 50%;
                margin-top: 9px;
                flex-shrink: 0;
                transition: all 0.3s ease;
              }
              .pod-feature-item:hover .pod-bullet {
                transform: scale(1.5);
                box-shadow: 0 0 4px #067bb1;
              }

              /* 13. Outcome Section */
              .pod-outcome-box {
                border-top: 1px solid #27272a; /* zinc-800 */
                padding-top: 24px;
                transition: border-color 0.5s ease;
                position: relative;
                z-index: 2;
              }
              .pod-dark-card:hover .pod-outcome-box {
                border-color: rgba(6,123,177,0.3);
              }
              .pod-outcome-label {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                background: rgba(6,123,177,0.1);
                border-radius: 6px;
                color: #fff;
                font-size: 11px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                margin-bottom: 12px;
              }
              .pod-outcome-dot {
                width: 4px;
                height: 4px;
                background-color: #067bb1;
                border-radius: 50%;
              }
              .pod-outcome-text {
                color: #d1d5db; /* gray-300 */
                font-size: 13px;
                line-height: 1.5;
              }

              /* 14. Bottom Gradient Glow */
              .pod-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 128px;
                background: linear-gradient(to top, rgba(6,123,177,0.05), transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
                z-index: 0;
              }
              .pod-dark-card:hover .pod-bottom-glow {
                opacity: 1;
              }

              /* Background Glow Effect */
              .section-glow-top {
                position: absolute;
                top: 0;
                left: 50%;
                transform: translateX(-50%);
                width: 600px;
                height: 300px;
                // background: radial-gradient(ellipse at center, rgba(238, 0, 0, 0.15), transparent 70%);
                pointer-events: none;
                z-index: 1;
              }

              /* Prime + Partner Model Section */
              .prime-partner-section {
                padding: 70px 0 80px 0;
                background-color: #fff;
                position: relative;
                overflow: hidden;
              }
              
              /* 1. Pulsing Ambient Orbs */
              .prime-orb-large {
                position: absolute;
                width: 600px;
                height: 600px;
                background: radial-gradient(circle, rgba(6,123,177,0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(150px);
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                z-index: 0;
                animation: primePulse 4s ease-in-out infinite;
              }
              .prime-orb-small {
                position: absolute;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(6,123,177,0.03) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(120px);
                top: 0;
                right: 0;
                z-index: 0;
                animation: primePulse 5s ease-in-out infinite 1.5s;
              }
              @keyframes primePulse {
                0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
                50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
              }
              
              /* Header Badge */
              .prime-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background: rgba(255, 255, 255, 0.8);
                backdrop-filter: blur(4px);
                border: 1px solid rgba(6,123,177,0.2);
                border-radius: 9999px;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                margin-bottom: 24px;
              }
              .prime-badge-dot {
                width: 8px;
                height: 8px;
                background-color: #067bb1;
                border-radius: 50%;
                position: relative;
              }
              .prime-badge-dot::after {
                content: '';
                position: absolute;
                top: -4px;
                left: -4px;
                right: -4px;
                bottom: -4px;
                background-color: #067bb1;
                border-radius: 50%;
                opacity: 0.4;
                animation: primePing 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
              }
              @keyframes primePing {
                75%, 100% { transform: scale(2); opacity: 0; }
              }
              .prime-badge-text {
                font-size: 14px;
                font-weight: 600;
                color: #334155;
                letter-spacing: 0.025em;
              }

              /* Typography */
              .prime-partner-title {
                color: #000;
                font-size: 42px;
                font-weight: 800;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
                line-height: 1.1;
              }
              .prime-partner-subtitle {
               font-size: 18px;
  color: #475569;
  max-width: 700px;
  margin: 0 auto 16px;
    margin-bottom: 16px;
  font-weight: 500;
              }

              /* Card Styling */
              .prime-partner-card {
                background: #fff;
                border-radius: 24px;
                padding: 40px;
                height: 100%;
                position: relative;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                border: 2px solid #e5e7eb;
                transition: all 0.5s ease;
                z-index: 1;
                overflow: hidden;
              }
              /* 7. Main Card Hover */
              .group:hover .prime-partner-card {
                border-color: rgba(6,123,177,0.5);
                box-shadow: 0 25px 50px -12px rgba(6,123,177,0.15);
                transform: translateY(-5px);
              }

              /* 6. Outer Glow Halo */
              .prime-card-halo {
                position: absolute;
                inset: -1px;
                // background: linear-gradient(135deg, #067bb1, #04467a);
                opacity: 0;
                filter: blur(24px);
                border-radius: 24px;
                z-index: -1;
                transition: opacity 0.7s ease;
              }
              .group:hover .prime-card-halo {
                opacity: 0.2;
              }

              /* 8. Diagonal Shine Overlay */
              .prime-shine-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(135deg, rgba(6,123,177,0.05) 0%, transparent 60%);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
              }
              .group:hover .prime-shine-overlay {
                opacity: 1;
              }

              /* 9. Top Accent Line */
              .prime-top-line {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
              }
              .group:hover .prime-top-line {
                opacity: 1;
              }

              /* 10. Decorative Corner Shape */
              .prime-corner-shape {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 20px;
                height: 20px;
                border-top: 2px solid rgba(6,123,177,0.1);
                border-right: 2px solid rgba(6,123,177,0.1);
                border-top-right-radius: 12px;
                transition: all 0.5s ease;
              }
              .group:hover .prime-corner-shape {
                border-color: rgba(6,123,177,0.3);
                transform: translate(2px, -2px);
              }

              /* 11. Icon Glow */
              .prime-icon-glow {
                position: absolute;
                inset: 0;
                background: #067bb1;
                filter: blur(20px);
                opacity: 0.3;
                border-radius: 50%;
                z-index: -1;
                transition: opacity 0.5s ease;
              }
              .group:hover .prime-icon-glow {
                opacity: 0.6;
              }

              /* 12. Icon Container */
              .prime-card-header-row {
                display: flex;
                align-items: center;
                gap: 20px;
                margin-bottom: 32px;
              }
              .prime-header-text-col {
                display: flex;
                flex-direction: column;
                justify-content: center;
              }

              .prime-icon-box {
                width: 64px;
                height: 64px;
                border-radius: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                transition: transform 0.5s ease;
                box-shadow: 0 4px 12px rgba(6, 123, 177, 0.2);
                flex-shrink: 0;
              }
              .group:hover .prime-icon-box {
                transform: scale(1.1);
              }
              .prime-icon-box.red {
                background: linear-gradient(135deg, #067bb1, #04467a);
                color: #fff;
              }
              .prime-icon-box.dark-red {
                background: linear-gradient(135deg, #067bb1, #04467a);
                color: #fff;
              }

              /* 13. Company Name */
              .prime-card-brand {
                font-size: 28px;
                font-weight: 800;
                color: #000;
                margin: 0 0 4px 0;
                transition: color 0.3s ease;
                line-height: 1;
              }
              .group:hover .prime-card-brand {
                color: #067bb1;
              }

              /* 14. Role Label */
              .prime-role-label {
                display: flex;
                align-items: center;
                gap: 8px;
                font-weight: 700;
                font-size: 13px;
                letter-spacing: 0.05em;
                text-transform: uppercase;
              }
              .prime-role-line {
                width: 24px;
                height: 2px;
              }
              .prime-role-text {
                transition: color 0.3s ease;
              }
              /* Comsense specifics */
              .prime-role-comsense .prime-role-line { background-color:#067bb1; }
              .prime-role-comsense .prime-role-text { color: #028bc6 }
              /* SRKAY specifics */
              .prime-role-srkay .prime-role-line { background-color:#067bb1; }
              .prime-role-srkay .prime-role-text { color: #028bc6; }

              /* List Items */
              .prime-list {
                list-style: none;
                padding: 0;
                margin: 0;
              }
              .prime-list-item {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 20px;
                color: #374151;
                font-weight: 500;
                font-size: 15px;
                line-height: 1.5;
                transition: color 0.3s ease;
                position: relative;
              }
              .prime-list-item:hover {
                color: #000;
              }

              /* 15. Bullet Point Animations */
              .prime-bullet-container {
                position: relative;
                width: 12px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
              }
              .prime-bullet {
                width: 4px; /* Base size */
                height: 4px;
                background-color: #067BB1;
                border-radius: 50%;
                transition: all 0.3s ease;
              }
              .prime-list-item:hover .prime-bullet {
                transform: scale(1.5) translateY(1px); /* 1.5px -> 2px roughly */
              }
              .prime-bullet-glow {
                position: absolute;
                width: 12px;
                height: 12px;
                background-color: #CDE4ED;
                border-radius: 50%;
                filter: blur(4px);
                opacity: 0;
                transition: opacity 0.3s ease;
                top: 6px;
                left: 0;
              }
              .prime-list-item:hover .prime-bullet-glow {
                opacity: 1;
              }

              /* Bottom Glow Effect */
              // .prime-bottom-glow {
              //   position: absolute;
              //   bottom: 0;
              //   left: 0;
              //   right: 0;
              //   height: 128px;
              //   background: linear-gradient(to top, rgba(227, 24, 55, 0.05), transparent);
              //   opacity: 0;
              //   transition: opacity 0.5s ease;
              //   pointer-events: none;
              // }
              .group:hover .prime-bottom-glow {
                opacity: 1;
              }

              /* Proven Outcomes Section */
              .proven-outcomes-section {
                padding: 70px 0 80px 0;
                background-color: #e2f1fe; /* gray-50 */
                position: relative;
                overflow: hidden;
              }
              
              .outcome-title {
                color: #000;
                font-size: 42px; /* 6xl */
                font-weight: 800;
                margin-bottom: 24px;
                letter-spacing: -0.02em;
                line-height: 1.1;
              }

              /* Orbs */
              .outcome-orb-left {
                position: absolute;
                width: 500px;
                height: 500px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(150px);
                top: 40%;
                left: -100px;
                z-index: 0;
                animation: primePulse 4s ease-in-out infinite;
              }
              .outcome-orb-right {
                position: absolute;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.03) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(120px);
                top: 10%;
                right: -50px;
                z-index: 0;
                animation: primePulse 5s ease-in-out infinite 1.5s;
              }

              /* Card Styles */
              .outcome-card {
                background: #fff;
                border-radius: 24px;
                padding: 40px;
                height: 100%;
                position: relative;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                border: 2px solid #e6f0ff;
                transition: all 0.5s ease;
                z-index: 1;
                overflow: hidden;
              }
              .group:hover .outcome-card {
                border-color: rgba(6, 123, 177, 0.5);
                box-shadow: 0 25px 50px -12px rgba(6, 123, 177, 0.15);
                transform: translateY(-5px);
              }

              /* Decorative Elements */
              .outcome-card-halo {
                position: absolute;
                inset: -1px;
                background: 0 20px 60px -10px rgba(6, 123, 177, 0.1);
                opacity: 0;
                filter: blur(24px);
                border-radius: 24px;
                z-index: -1;
                transition: opacity 0.7s ease;
              }
              .group:hover .outcome-card-halo { opacity: 0.2; }

              .outcome-shine-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(135deg, rgba(6, 123, 177, 0.05) 0%, transparent 60%);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
              }
              .group:hover .outcome-shine-overlay { opacity: 1; }

              .outcome-top-line {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
              }
              .group:hover .outcome-top-line { opacity: 1; }

              .outcome-corner-shape {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 20px;
                height: 20px;
                border-top: 2px solid rgba(6, 123, 177, 0.1);
                border-right: 2px solid rgba(6, 123, 177, 0.1);
                border-top-right-radius: 12px;
                transition: all 0.5s ease;
              }
              .group:hover .outcome-corner-shape {
                border-color: rgba(6, 123, 177, 0.3);
                transform: translate(2px, -2px);
              }

              .outcome-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 128px;
                background: linear-gradient(to top, rgba(6, 123, 177, 0.05), transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
              }
              .group:hover .outcome-bottom-glow { opacity: 1; }

              /* Header Row */
              .outcome-header-row {
                display: flex;
                align-items: center;
                gap: 20px;
                margin-bottom: 32px;
              }
              .outcome-header-text {
                display: flex;
                flex-direction: column;
                justify-content: center;
                flex: 1;
              }

              /* Icon Box */
              .outcome-icon-box {
                width: 64px;
                height: 64px;
                border-radius: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                transition: transform 0.5s ease;
                box-shadow: 0 10px 15px -3px rgba(6,123,177,0.3);
                flex-shrink: 0;
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
                color: #fff;
              }
              .group:hover .outcome-icon-box { transform: scale(1.1); }
              
              .outcome-icon-glow {
                position: absolute;
                inset: 0;
                background: #067bb1;
                filter: blur(20px);
                opacity: 0.3;
                border-radius: 50%;
                z-index: -1;
                transition: opacity 0.5s ease;
              }
              .group:hover .outcome-icon-glow { opacity: 0.6; }

              /* Title & Role */
              .outcome-card-title {
                font-size: 28px;
                font-weight: 800;
                color: #000;
                margin: 0 0 4px 0;
                transition: color 0.3s ease;
                line-height: 1.2;
              }
              .group:hover .outcome-card-title { color: #067bb1; }

              .outcome-role-label {
                display: flex;
                align-items: center;
                gap: 8px;
                font-weight: 700;
                font-size: 13px;
                letter-spacing: 0.05em;
                text-transform: uppercase;
              }
              .outcome-role-line {
                width: 24px;
                height: 2px;
                background-color: #067bb1;
              }
              .outcome-role-text {
                color: #067bb1;
              }

              /* Metric Rows (Micro-Interactions) */
              .outcome-metrics {
                display: flex;
                flex-direction: column;
                gap: 16px;
              }
              .outcome-metric-row {
                display: flex;
                align-items: center;
                gap: 16px;
                padding: 16px;
                background-color: #f9fafb; /* gray-50 */
                border: 1px solid transparent;
                border-radius: 12px;
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
              }
              .outcome-metric-row:hover {
                background-color: #fff;
                border-color: rgba(6, 123, 177, 0.3);
                box-shadow: 0 0 20px rgba(6, 123, 177, 0.1);
              }

              /* Metric Icon */
              .outcome-metric-icon {
                width: 40px;
                height: 40px;
                border-radius: 10px;
                background-color: #fff;
                border: 1px solid #e5e7eb;
                color: #6b7280;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.3s ease;
              }
              .outcome-metric-row:hover .outcome-metric-icon {
                background-color: #eff6ff;
                border-color: #bfdbfe;
                color: #067bb1;
                transform: scale(1.1);
                box-shadow: 0 0 10px rgba(6, 123, 177, 0.2);
              }

              /* Metric Text */
              .outcome-metric-label {
                flex-grow: 1;
                font-size: 15px;
                font-weight: 500;
                color: #4b5563;
                transition: color 0.3s ease;
              }
              .outcome-metric-row:hover .outcome-metric-label {
                color: #111827;
              }

              .outcome-metric-value {
                font-size: 16px;
                font-weight: 700;
                color: #067bb1;
                transition: transform 0.3s ease;
              }
              .outcome-metric-row:hover .outcome-metric-value {
                transform: scale(1.1);
              }

              /* 90-Day Launch Sprint Section */
              .launch-sprint-section {
                padding: 70px 0 80px 0;
                background-color: #fff; /* gray-50 */
                position: relative;
                overflow: hidden;
              }

              .launch-sprint-section.faq-section {
                padding: 70px 0 80px 0;
                background-color: #fff; /* gray-50 */
                position: relative;
                overflow: hidden;
              }
              
              /* Background Orbs */
              // .launch-bg-orb-large {
              //   position: absolute;
              //   top: 10%;
              //   left: -10%;
              //   width: 500px;
              //   height: 500px;
              //   background: radial-gradient(circle, rgba(227, 24, 55, 0.08) 0%, transparent 70%);
              //   border-radius: 50%;
              //   filter: blur(60px);
              //   animation: pulseGlow 8s infinite ease-in-out;
              //   z-index: 0;
              // }
              .launch-bg-orb-small {
                position: absolute;
                bottom: 10%;
                right: -5%;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.08) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(50px);
                animation: pulseGlow 6s infinite ease-in-out reverse;
                z-index: 0;
              }

              /* Container Design */
              .launch-container {
                position: relative;
                background: linear-gradient(180deg, #effdff 0%, #effdff 100%); /* zinc-900 to black */
                border-radius: 24px;
                padding: 60px 40px;
border: 1px solid rgba(56, 164, 229, 0.5);
  box-shadow: 0 0 0 1px rgba(24, 170, 227, 0.2), 0 20px 50px -12px rgba(24, 132, 227, 0.25);
                overflow: hidden;
              }
              
              .launch-grid-bg {
                position: absolute;
                inset: 0;
                background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                                  linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
                background-size: 24px 24px;
                opacity: 0.3;
                z-index: 0;
                pointer-events: none;
              }
              
              .launch-border-glow {
                position: absolute;
                inset: -20px;
                border-radius: 24px;
                // background: radial-gradient(circle at 50% 0%, rgba(227, 24, 55, 0.15), transparent 70%);
                z-index: 0;
                pointer-events: none;
              }
              
              /* Internal Pulsing Orbs */
              .launch-orb-1 {
                position: absolute;
                top: 20%;
                left: 20%;
                width: 300px;
                height: 300px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.1) 0%, transparent 70%);
                filter: blur(40px);
                animation: pulseGlow 10s infinite ease-in-out;
                z-index: 0;
              }
              .launch-orb-2 {
                position: absolute;
                bottom: 20%;
                right: 20%;
                width: 250px;
                height: 250px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.08) 0%, transparent 70%);
                filter: blur(40px);
                animation: pulseGlow 12s infinite ease-in-out reverse;
                z-index: 0;
              }
              
              .launch-top-accent {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, rgb(221, 244, 255), transparent);
                box-shadow: 0 1px 10px rgb(239, 253, 255);
                z-index: 2;
              }
              
              .launch-corner-tr {
                position: absolute;
                top: 20px;
                right: 20px;
                width: 40px;
                height: 40px;
border-top: 2px solid rgb(221, 244, 255);
  border-right: 2px solid rgb(221, 244, 255);
                border-top-right-radius: 8px;
                z-index: 2;
              }
              
              .launch-corner-bl {
                position: absolute;
                bottom: 20px;
                left: 20px;
                width: 40px;
                height: 40px;
                border-bottom: 2px solid rgb(221, 244, 255);
                border-left: 2px solid rgb(221, 244, 255);
                border-bottom-left-radius: 8px;
                z-index: 2;
              }
              
              .launch-content {
                position: relative;
                z-index: 1;
              }

              /* Header */
              .launch-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 6px 16px;
             background: rgba(72, 167, 251, 0.1);
  border: 1px solid rgba(24, 122, 227, 0.3);
  border-radius: 999px;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
              }
              
              .launch-badge-dot {
                width: 6px;
                height: 6px;
                background-color: #067bb1;
                border-radius: 50%;
                box-shadow: 0 0 8px #067bb1;
                animation: pulseGlow 2s infinite;
              }
              
              .launch-title {
                font-size: 42px;
                font-weight: 800;
                color: #000;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
              }
              
              .launch-subtitle {
                font-size: 18px;
                max-width: 600px;
                margin: 0 auto;
              }

              /* Card Styles */
              .launch-card {
                background: rgba(255, 255, 255, 0.03);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 16px;
                padding: 32px;
                position: relative;
                overflow: hidden;
                backdrop-filter: blur(10px);
                transition: all 0.5s ease;
                background: linear-gradient(135deg, #067bb1 0%, #04467a 100%);
  border: 1px solid #067bb1;
              }
              
              .launch-card:hover {
                // border-color: rgba(227, 24, 55, 0.5);
                transform: translateY(-4px);
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
              }
              
              .launch-card-halo {
                position: absolute;
                inset: -2px;
                border-radius: 18px;
                // background: radial-gradient(circle at 50% 0%, rgba(227, 24, 55, 0.3), transparent 70%);
                opacity: 0;
                transition: opacity 0.7s ease;
                z-index: 0;
                pointer-events: none;
              }
              .launch-card:hover .launch-card-halo {
                opacity: 1;
              }
              
              .launch-card-shine {
                position: absolute;
                top: 0;
                left: -100%;
                width: 200%;
                height: 100%;
                background: linear-gradient(115deg, transparent 40%, rgba(255, 255, 255, 0.05) 45%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 55%, transparent 60%);
                transition: left 0.5s ease;
                z-index: 0;
                pointer-events: none;
                opacity: 0;
              }
              .launch-card:hover .launch-card-shine {
                left: 100%;
                opacity: 1;
                transition: left 0.7s ease;
              }

              /* Left Card Specifics */
              .launch-icon-wrapper {
                position: relative;
                width: 64px;
                height: 64px;
                /* margin: 0 auto; Removed for left alignment */
              }
              
              .launch-icon-box {
                width: 100%;
                height: 100%;
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                position: relative;
                z-index: 2;
                transition: all 0.5s ease;
              }
              .launch-card:hover .launch-icon-box {
                transform: scale(1.1);
                border-color: #effdff;
                color: #effdff;
              }
              
              .launch-icon-glow {
                position: absolute;
                inset: 0;
                background: #067bb1;
                filter: blur(20px);
                opacity: 0.5;
                transition: opacity 0.5s ease, filter 0.5s ease;
                z-index: 1;
              }
              .launch-card:hover .launch-icon-glow {
                opacity: 1;
                filter: blur(25px);
              }
              
              .launch-days-number {
                font-size: 40px;
                font-weight: 800;
                color: #fff;
                line-height: 1;
                margin-bottom: 0;
                transition: color 0.3s ease;
              }
              .launch-card:hover .launch-days-number {
                color: #fff;
              }
              
              .launch-days-label {
                font-size: 16px;
                color: #fff;
                font-weight: 500;
                transition: color 0.3s ease;
              }
              .launch-card:hover .launch-days-label {
                color: #fff;
              }
              
              .launch-divider {
                width: 100%;
                height: 1px;
                background: rgba(255, 255, 255, 0.15);
                margin: 24px 0;
                transition: background-color 0.3s ease;
              }
              .launch-card:hover .launch-divider {
                background: #effdff;
              }
              
              .launch-days-desc {
                font-size: 18px;
                font-weight: 600;
                color: #fff;
              }
              
              .launch-card-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 2px;
linear-gradient(90deg, transparent, #EDFBFD, transparent)
                opacity: 0;
                transition: opacity 0.5s ease;
                box-shadow: 0 -5px 15px rgba(227, 24, 55, 0.3);
              }
              .launch-card:hover .launch-card-bottom-glow {
                opacity: 1;
              }
              
              .launch-foundation-text {
                font-size: 14px;
                color: #fff !important; /* zinc-500 */
              }

              /* Right Card Specifics */
              .launch-deliverables-title {
                font-size: 24px;
                font-weight: 700;
                color: #fff;
                transition: color 0.3s ease;
              }
              .launch-card:hover .launch-deliverables-title {
                color: #fff;
              }
              
              .launch-list {
                list-style: none;
                padding: 0;
                margin: 0;
              }
              
              .launch-list-item {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 0px;
                padding: 8px;
                border-radius: 8px;
                transition: background-color 0.3s ease;
              }
              .launch-list-item:hover {
                background: rgba(255, 255, 255, 0.05);
              }
              
              .launch-check-icon {
                position: relative;
                color: #fff;
                flex-shrink: 0;
                margin-top: 2px;
                transition: transform 0.3s ease;
              }
              .launch-list-item:hover .launch-check-icon {
                transform: scale(1.2);
              }
              
              .launch-check-glow {
                position: absolute;
                inset: 0;
                background: #066EA3;
                filter: blur(8px);
                opacity: 0;
                transition: opacity 0.3s ease;
                border-radius: 50%;
                z-index: -1;
              }
              .launch-list-item:hover .launch-check-glow {
                opacity: 0.6;
              }
              
              .launch-list-text {
                font-size: 15px;
                color: #fff; /* zinc-300 */
                line-height: 1.5;
                transition: color 0.3s ease;
              }
              .launch-list-item:hover .launch-list-text {
                color: #fff;
              }

              /* Why Enterprises Choose Comsense Section */
              .why-section {
                padding: 70px 0 80px 0;
                background-color: #e2f1fe; /* gray-50 */
                position: relative;
                overflow: hidden;
              }
              
              .why-orb-1 {
                position: absolute;
                top: 5%;
                left: -10%;
                width: 500px;
                height: 500px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(60px);
                animation: pulseGlow 8s infinite ease-in-out;
                z-index: 0;
              }
              
              .why-orb-2 {
                position: absolute;
                bottom: 10%;
                right: -5%;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(50px);
                animation: pulseGlow 10s infinite ease-in-out reverse;
                z-index: 0;
              }
              
              .why-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 6px 16px;
                background: #fff;
                border: 1px solid rgba(6, 123, 177, 0.2);
                border-radius: 999px;
                color: #028bc6;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
                text-transform: uppercase;
                box-shadow: 0 2px 10px rgba(6, 123, 177, 0.08);
              }
              
              .why-badge-dot {
                width: 6px;
                height: 6px;
                background-color: #067bb1;
                border-radius: 50%;
                box-shadow: 0 0 8px rgba(6, 123, 177, 0.6);
                animation: pulseGlow 2s infinite;
              }
              
              .why-title {
                font-size: 42px;
                font-weight: 800;
                color: #111827; /* gray-900 */
                letter-spacing: -0.02em;
                margin-bottom: 16px;
              }
              
              .why-subtitle {
                font-size: 18px;
                color: #6b7280; /* gray-500 */
                max-width: 600px;
                margin: 0 auto;
                line-height: 1.6;
              }
              
              .why-card {
                background: #fff;
                border: 1px solid #e5e7eb; /* gray-200 */
                border-radius: 24px;
                padding: 40px 32px;
                position: relative;
                overflow: hidden;
                transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                height: 100%;
                z-index: 1;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border: 2px solid #e5e7eb;
              }
              
              .why-card:hover {
                border-color: rgba(6, 123, 177, 0.3);
                transform: translateY(-2px);
                box-shadow: 0 20px 40px -12px rgba(6, 123, 177, 0.15);
              }
              
              .why-card-halo {
                position: absolute;
                inset: -2px;
                border-radius: 26px;
                // background: radial-gradient(circle at 50% 0%, rgba(6, 123, 177, 0.2), transparent 70%);
                opacity: 0;
                transition: opacity 0.7s ease;
                z-index: -1;
                pointer-events: none;
              }
              .why-card:hover .why-card-halo {
                opacity: 1;
              }
              
              .why-card-shine {
                position: absolute;
                top: 0;
                left: -100%;
                width: 200%;
                height: 100%;
                background: linear-gradient(115deg, transparent 40%, rgba(6, 123, 177, 0.03) 45%, rgba(6, 123, 177, 0.06) 50%, rgba(6, 123, 177, 0.03) 55%, transparent 60%);
                transition: left 0.5s ease;
                z-index: 0;
                pointer-events: none;
                opacity: 0;
              }
              .why-card:hover .why-card-shine {
                left: 100%;
                opacity: 1;
              }
              
              .why-accent-line {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
                z-index: 2;
              }
              .why-card:hover .why-accent-line {
                opacity: 1;
              }
              
              .why-corner-l {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 50px;
                height: 50px;
                border-top: 2px solid rgba(6, 123, 177, 0.2);
                border-right: 2px solid rgba(6, 123, 177, 0.2);
                border-top-right-radius: 20px;
                opacity: 1;
                transition: border-color 0.3s ease;
                z-index: 2;
                pointer-events: none;
              }
              .why-card:hover .why-corner-l {
                border-color: rgba(6, 123, 177, 0.6);
              }
              
              .why-corner-orb {
                position: absolute;
                top: -20px;
                right: -20px;
                width: 100px;
                height: 100px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.15), transparent 70%);
                border-radius: 50%;
                opacity: 0;
                transition: opacity 0.5s ease;
                z-index: 0;
              }
              .why-card:hover .why-corner-orb {
                opacity: 1;
              }
              
              .why-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                box-shadow: 0 -10px 30px rgba(6, 123, 177, 0.15);
                opacity: 0;
                transition: opacity 0.5s ease;
              }
              .why-card:hover .why-bottom-glow {
                opacity: 1;
              }
              
              .why-icon-box {
                width: 64px;
                height: 64px;
                border-radius: 16px;
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
                box-shadow: 0 4px 12px rgba(6, 123, 177, 0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 24px;
                color: #fff;
                position: relative;
                z-index: 2;
                transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
                border: 1px solid rgba(6, 123, 177, 0.25);
              }
              .why-card:hover .why-icon-box {
                transform: scale(1.1);
                box-shadow: 0 10px 25px rgba(6, 123, 177, 0.4);
              }
              
              .why-icon-glow {
                position: absolute;
                inset: 0;
                background: rgba(6,123,177,0.4);
                filter: blur(15px);
                opacity: 0;
                transition: opacity 0.5s ease;
                z-index: -1;
                border-radius: 16px;
              }
              .why-card:hover .why-icon-glow {
                opacity: 0.6;
              }
              
              .why-card-title {
                font-size: 20px;
                font-weight: 700;
                color: #111827; /* gray-900 */
                margin-bottom: 12px;
                transition: color 0.3s ease;
              }
              .why-card:hover .why-card-title {
                color: #067bb1;
              }
              
              .why-card-desc {
                font-size: 15px;
                color: #6b7280; /* gray-500 */
                line-height: 1.6;
                transition: color 0.3s ease;
                margin-bottom: 0px;
              }
              .why-card:hover .why-card-desc {
                color: #1f2937; /* gray-800 */
              }

              /* Connect With Us Section */
              .connect-section {
                padding: 20px 0 100px 0;
                background-color: #ffffff;
                position: relative;
                overflow: hidden;
              }

              .connect-orb-1 {
                position: absolute;
                top: 50%;
                left: 10%;
                width: 500px;
                height: 500px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(60px);
                animation: pulseGlow 8s infinite ease-in-out;
                z-index: 0;
                transform: translateY(-50%);
              }

              .connect-orb-2 {
                position: absolute;
                top: 50%;
                right: 10%;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(50px);
                animation: pulseGlow 10s infinite ease-in-out reverse;
                z-index: 0;
                transform: translateY(-50%);
              }

              .connect-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 6px 16px;
                background: #fff;
                border: 1px solid rgba(227, 24, 55, 0.2);
                border-radius: 999px;
                color: #ee0000;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
                box-shadow: 0 2px 10px rgba(227, 24, 55, 0.05);
              }

              .connect-badge-dot {
                width: 6px;
                height: 6px;
                background-color: #E31837;
                border-radius: 50%;
                box-shadow: 0 0 8px #E31837;
                animation: pulseGlow 2s infinite;
              }

              .connect-title {
                font-size: 42px;
                font-weight: 800;
                color: #111827;
                letter-spacing: -0.02em;
                margin-bottom: 16px;
                line-height: 1.1;
              }

              .connect-subtitle {
                font-size: 18px;
                color: #6b7280;
                max-width: 600px;
                margin: 0 auto 60px;
                line-height: 1.6;
              }

              /* Contact Cards */
              .connect-card {
                background: #fff;
                border: 1px solid #e5e7eb;
                border-radius: 24px;
                padding: 32px;
                display: flex;
                align-items: center;
                gap: 24px;
                position: relative;
                overflow: hidden;
                transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                height: 100%;
                z-index: 1;
              }

              .connect-card:hover {
                border-color: rgba(227, 24, 55, 0.5);
                transform: translateY(-4px);
                box-shadow: 0 20px 40px -12px rgba(227, 24, 55, 0.15);
              }

              .connect-card-halo {
                position: absolute;
                inset: -2px;
                border-radius: 26px;
                background: radial-gradient(circle at 50% 50%, rgba(227, 24, 55, 0.2), transparent 70%);
                opacity: 0;
                transition: opacity 0.7s ease;
                z-index: -1;
                pointer-events: none;
              }
              .connect-card:hover .connect-card-halo {
                opacity: 1;
              }

              .connect-card-shine {
                position: absolute;
                top: 0;
                left: -100%;
                width: 200%;
                height: 100%;
                background: linear-gradient(115deg, transparent 40%, rgba(227, 24, 55, 0.05) 45%, rgba(227, 24, 55, 0.1) 50%, rgba(227, 24, 55, 0.05) 55%, transparent 60%);
                transition: left 0.5s ease;
                z-index: 0;
                pointer-events: none;
                opacity: 0;
              }
              .connect-card:hover .connect-card-shine {
                left: 100%;
                opacity: 1;
              }

              .connect-icon-box {
                width: 64px;
                height: 64px;
                border-radius: 16px;
                background: linear-gradient(135deg, #ee0000, #ee0000);
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                position: relative;
                z-index: 2;
                transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
                flex-shrink: 0;
                box-shadow: 0 4px 12px rgba(227, 24, 55, 0.3);
              }

              .connect-card:hover .connect-icon-box {
                transform: scale(1.1);
                box-shadow: 0 8px 20px rgba(227, 24, 55, 0.5);
              }

              .connect-card-content {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                z-index: 2;
              }

              .connect-label {
                font-size: 13px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                color: #6b7280;
                margin-bottom: 4px;
              }

              .connect-value {
                font-size: 18px;
                font-weight: 700;
                color: #111827;
                transition: color 0.3s ease;
              }
              .connect-card:hover .connect-value {
                color: #ee0000;
              }

              .connect-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 2px;
                background: linear-gradient(90deg, transparent, #E31837, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
              }
              .connect-card:hover .connect-bottom-glow {
                opacity: 1;
              }

              /* CTA Card */
              .connect-cta-card {
                background: #fff;
                border: 1px solid rgba(227, 24, 55, 0.3);
                border-radius: 24px;
                padding: 60px 40px;
                position: relative;
                overflow: hidden;
                box-shadow: 0 20px 60px -10px rgba(227, 24, 55, 0.1);
                margin-top: 40px;
                z-index: 1;
              }

              .connect-cta-halo {
                position: absolute;
                inset: -2px;
                border-radius: 26px;
                background: radial-gradient(circle at 50% 0%, rgba(227, 24, 55, 0.15), transparent 70%);
                z-index: -1;
                pointer-events: none;
              }

              .connect-cta-accent {
                position: absolute;
                top: 0;
                left: 20%;
                right: 20%;
                height: 1px;
                background: linear-gradient(90deg, transparent, #E31837, transparent);
                opacity: 0.5;
              }

              .connect-corner-tr {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 40px;
                height: 40px;
                border-top: 2px solid rgba(227, 24, 55, 0.2);
                border-right: 2px solid rgba(227, 24, 55, 0.2);
                border-top-right-radius: 16px;
              }

              .connect-corner-bl {
                position: absolute;
                bottom: 24px;
                left: 24px;
                width: 40px;
                height: 40px;
                border-bottom: 2px solid rgba(227, 24, 55, 0.2);
                border-left: 2px solid rgba(227, 24, 55, 0.2);
                border-bottom-left-radius: 16px;
              }

              .connect-cta-title {
                font-size: 36px;
                font-weight: 800;
                color: #111827;
                margin-bottom: 16px;
              }

              .connect-cta-desc {
                font-size: 18px;
                color: #4b5563;
                max-width: 600px;
                margin: 0 auto 40px;
                line-height: 1.6;
              }

              .connect-btn-primary {
                background: linear-gradient(135deg, #ee0000, #ee0000);
                color: white;
                padding: 14px 32px;
                border-radius: 12px;
                font-weight: 700;
                border: none;
                position: relative;
                overflow: hidden;
                transition: all 0.3s ease;
                box-shadow: 0 4px 12px rgba(227, 24, 55, 0.3);
                text-decoration: none;
                display: inline-block;
              }
              .connect-btn-primary:hover {
                transform: scale(1.05);
                box-shadow: 0 8px 20px rgba(227, 24, 55, 0.5);
                color: white;
              }
              .connect-btn-primary:active {
                transform: scale(0.95);
              }

              .connect-btn-secondary {
                background: #fff;
                color: #111827;
                padding: 14px 32px;
                border-radius: 12px;
                font-weight: 700;
                border: 1px solid #e5e7eb;
                position: relative;
                overflow: hidden;
                transition: all 0.3s ease;
                text-decoration: none;
                display: inline-block;
                margin-left: 16px;
              }
              .connect-btn-secondary:hover {
                border-color: #E31837;
                color: #E31837;
                transform: scale(1.05);
                box-shadow: 0 4px 12px rgba(227, 24, 55, 0.1);
              }
              .connect-btn-secondary:active {
                transform: scale(0.95);
              }

              @media (max-width: 768px) {
                .connect-title { font-size: 42px; }
                .connect-card { flex-direction: column; text-align: center; padding: 24px; }
                .connect-card-content { align-items: center; }
                .connect-cta-card { padding: 40px 24px; }
                .connect-btn-secondary { margin-left: 0; margin-top: 16px; display: block; width: 100%; }
                .connect-btn-primary { display: block; width: 100%; }
              }

              /* Trusted Section */
              .trusted-section {
                padding: 70px 0 80px 0;
                padding: 0px 0 0px 0;
                background: #fff;
                position: relative;
                overflow: hidden;
              }

              .trusted-orb {
                position: absolute;
                width: 600px;
                height: 600px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.03) 0%, transparent 70%);
                border-radius: 50%;
                pointer-events: none;
                z-index: 0;
              }
              .trusted-orb-1 { top: -10%; left: -10%; }
              .trusted-orb-2 { bottom: -10%; right: -10%; }

              .trusted-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background: #fff;
                border: 1px solid #ffd4d4;
                border-radius: 999px;
                color: #ee0000;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
                box-shadow: 0 4px 12px rgba(227, 24, 55, 0.08);
              }
              .trusted-badge-dot {
                width: 6px;
                height: 6px;
                background-color: #E31837;
                border-radius: 50%;
                box-shadow: 0 0 8px #E31837;
                animation: pulseGlow 2s infinite;
              }

              .trusted-title {
                font-size: 42px;
                font-weight: 800;
                color: #111827;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
              }

              .trusted-subtitle {
                font-size: 18px;
                color: #6b7280;
                margin-bottom: 60px;
              }

              .brand-container {
                background: rgba(255, 255, 255, 0.9);
                backdrop-filter: blur(10px);
                border: 1px solid #e5e7eb;
                border-radius: 24px;
                padding: 64px;
                position: relative;
                overflow: hidden;
                box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.05);
                transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                margin-bottom: 80px;
              }

              .brand-container::before {
                content: '';
                position: absolute;
                inset: -2px;
                border-radius: 26px;
                background: #e2f1fe;
                z-index: -1;
                // filter: blur(20px);
              }

              .brand-container:hover {
                border-color: rgba(227, 24, 55, 0.3);
                box-shadow: 0 24px 48px -12px rgba(227, 24, 55, 0.15);
              }

              .brand-accent-line {
                position: absolute;
                top: 0;
                left: 20%;
                right: 20%;
                height: 1px;
                background: linear-gradient(90deg, transparent, #E31837, transparent);
                opacity: 0.5;
              }

              .brand-corner-tr {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 24px;
                height: 24px;
                border-top: 2px solid #e5e7eb;
                border-right: 2px solid #e5e7eb;
                border-top-right-radius: 12px;
                transition: border-color 0.3s ease;
              }
              .brand-corner-bl {
                position: absolute;
                bottom: 24px;
                left: 24px;
                width: 24px;
                height: 24px;
                border-bottom: 2px solid #e5e7eb;
                border-left: 2px solid #e5e7eb;
                border-bottom-left-radius: 12px;
                transition: border-color 0.3s ease;
              }

              .brand-container:hover .brand-corner-tr,
              .brand-container:hover .brand-corner-bl {
                border-color: #E31837;
              }

              .brand-shine {
                position: absolute;
                top: 0;
                left: -100%;
                width: 200%;
                height: 100%;
                background: linear-gradient(115deg, transparent 40%, rgba(227, 24, 55, 0.03) 45%, rgba(227, 24, 55, 0.06) 50%, rgba(227, 24, 55, 0.03) 55%, transparent 60%);
                transition: left 0.7s ease;
                pointer-events: none;
              }
              .brand-container:hover .brand-shine {
                left: 100%;
              }

              .brand-logos-row {
                display: flex;
                justify-content: space-around;
                align-items: center;
                flex-wrap: wrap;
                gap: 40px;
              }
              .brand-logo-item {
                max-height: 48px;
                opacity: 0.7;
                transition: all 0.3s ease;
                // filter: grayscale(100%);
                max-width: 150px;
                object-fit: contain;
              }
              .brand-logo-item:hover {
                opacity: 1;
                filter: grayscale(0%);
                transform: scale(1.05);
              }

              .trusted-stat-item {
                text-align: center;
                padding: 24px;
                transition: transform 0.3s ease;
              }
              .trusted-stat-item:hover {
                transform: translateY(-5px);
              }
              .trusted-stat-value {
                font-size: 48px;
                font-weight: 800;
                color: #111827;
                margin-bottom: 8px;
                position: relative;
                display: inline-block;
                transition: color 0.3s ease, transform 0.3s ease;
              }
              .trusted-stat-item:hover .trusted-stat-value {
                color: #E31837;
                transform: scale(1.1);
              }
              .trusted-stat-value::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 120%;
                height: 120%;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.15) 0%, transparent 70%);
                opacity: 0;
                transition: opacity 0.3s ease;
                z-index: -1;
              }
              .trusted-stat-item:hover .trusted-stat-value::after {
                opacity: 1;
              }
              .trusted-stat-label {
                font-size: 16px;
                font-weight: 600;
                color: #6b7280;
              }

              .faq-card {
                background: #ffffff;
                color: #0f172a;
                border: 1px solid rgba(6, 123, 177, 0.12);
                box-shadow: 0 14px 40px rgba(15, 23, 42, 0.08);
                padding: 28px;
                min-height: 260px;
              }

              .faq-question {
                font-size: 1.05rem;
                font-weight: 700;
                color: #0f172a;
                line-height: 1.35;
              }

              .faq-answer {
                margin-bottom: 0;
                color: #334155;
                font-size: 0.95rem;
                line-height: 1.75;
              }

              .faq-card:not(:last-child) {
                margin-bottom: 0;
              }

              .faq-accordion.modern-faq-accordion .accordion-item {
                border: 1px solid rgba(6, 123, 177, 0.18);
                border-radius: 20px;
                background: #fff;
                box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
                overflow: hidden;
                margin-bottom: 18px;
              }

              .faq-accordion.modern-faq-accordion .accordion-button {
                background: #f7fbff;
                color: #0f172a;
                font-size: 1.05rem;
                font-weight: 700;
                padding: 22px 28px;
                border: none;
                border-radius: 0;
                transition: background 0.3s ease, color 0.3s ease;
              }

              .faq-accordion.modern-faq-accordion .accordion-button:not(.collapsed) {
                background: #e7f2ff;
                color: #04467a;
              }

              .faq-accordion.modern-faq-accordion .accordion-button::after {
                width: 1.2rem;
                height: 1.2rem;
              }

              .faq-accordion.modern-faq-accordion .accordion-body {
                padding: 20px 28px 26px;
                color: #334155;
                font-size: 0.96rem;
                line-height: 1.8;
                background: #fff;
              }

              .faq-accordion.modern-faq-accordion .accordion-item.active .accordion-button {
                box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.05);
              }

              .testimonial-card blockquote {
                font-size: 1rem;
                color: #0f172a;
                line-height: 1.8;
                margin: 0 0 12px 0;
                quotes: "“”‘’";
              }

              .testimonial-card .text-muted {
                color: #64748b !important;
                font-weight: 600;
              }

              .testimonial-carousel .carousel-indicators [data-bs-target] {
                background-color: rgba(6,123,177,0.85);
                width: 10px;
                height: 10px;
                border-radius: 50%;
              }

              .testimonial-carousel .carousel-control-prev-icon,
              .testimonial-carousel .carousel-control-next-icon {
                filter: invert(20%) sepia(80%) saturate(400%) hue-rotate(170deg) brightness(90%);
                background-size: 100%, 100%;
              }

              .testimonial-card.mx-auto {
                padding: 36px;
                border-radius: 16px;
                background: linear-gradient(180deg, #ffffff 0%, #f7fcff 100%);
                border: 1px solid rgba(6,123,177,0.06);
              }

              .testimonial-carousel {
                position: relative;
                padding-bottom: 36px;
                overflow: visible;
              }

              .testimonial-carousel .carousel-indicators {
                position: absolute;
                bottom: -22px;
                left: 50%;
                transform: translateX(-50%);
                justify-content: center;
                gap: 10px;
                margin: 0;
                padding: 0;
                width: auto;
              }

              .testimonial-carousel .carousel-control-prev,
              .testimonial-carousel .carousel-control-next {
                width: 56px;
                height: 56px;
                top: 50%;
                transform: translateY(-50%);
                opacity: 1;
                background: transparent;
                border-radius: 999px;
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .testimonial-carousel .carousel-control-prev {
                left: -72px;
              }

              .testimonial-carousel .carousel-control-next {
                right: -72px;
              }

              .testimonial-card:hover{
              box-shadow:none;
              }

              @media (max-width: 768px) {
                .trusted-title { font-size: 36px; }
                .brand-container { padding: 32px; }
                .brand-logos-row { gap: 24px; }
                .brand-logo-item { max-height: 32px; max-width: 100px; }
              }
              @media (max-width: 992px) {
                .hero-media { margin-top: 40px !important; }
              }
            `}</style>
      <div className="gcc-outfit">
        {/* Hero Section (Above the Fold) */}

        {/* Hero Section */}
        <header className="masthead z-0 pt-5 pb-5">
          <div className="container position-relative">
            <Image
              src={bannerShape1}
              className="z-n1 position-absolute top-0 end-0 opacity-25 w-40 d-none d-md-block"
              alt="decorative"
            />
            <Row className="align-items-center justify-content-between">
              <Col lg="7">
                <div className="pe-3">
                  <div className="hero-tag mb-3">
                    <Zap size={15} /> Outcome-Led GCC Builder
                  </div>
                  <h1 className="hero-title text-black mb-3">
                    While Others Are Still Setting Up,{" "}
                    <span className="highlight-red">
                      You Could Already Be Delivering.
                    </span>
                  </h1>
                  <p className="text-black ls-minus-045 fw-500 fs-5 mb-4 opacity-75">
                    Most GCCs spend 12 months getting ready. We help you deliver
                    in 90 days by starting with a production-ready team built
                    for outcomes.
                  </p>
                  <div className="hero-chip-row mb-4">
                    <div className="stat-card">
                      <div>
                        <div className="stat-value">• Launch in 90 days </div>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div>
                        <div className="stat-value">
                          • Deliver real business outcomes{" "}
                        </div>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div>
                        <div className="stat-value">• Scale to a 100+ team</div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-3 mt-4">
                    <button
                      type="button"
                      className="btn-red"
                      onClick={openDataReadinessModal}
                    >
                      Book a GCC Discovery Session
                    </button>
                    {/* <Link to="/about" className="btn-outline-dark">
                      Learn More <ArrowRight size={18} className="ms-2" />
                    </Link> */}
                  </div>
                </div>
              </Col>
              <Col lg="5" className="text-center">
                <div className="hero-media m-auto" style={{ width: "90%" }}>
                  <Image src={flutterHero} alt="GCC hero" className="w-100" />
                </div>
              </Col>
            </Row>
            <Image
              src={bannerShape2}
              className="position-absolute bottom-0 end-0 w-40 d-none d-md-block bannerShapeBottom"
              alt="decorative"
            />
          </div>
        </header>

        {/* The Problem Section */}
        <section
          className="problem-section"
          style={{ backgroundColor: "#e2f1fe" }}
        >
          <Container>
            <div className="text-center mb-5">
              {/* <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="problem-pill"
              >
                <span className="me-2">⚡</span> The Challenge
              </motion.div> */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="problem-title"
              >
                Why This Matters
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="problem-desc"
              >
                GCC strategy is evolving, but execution has yet to catch up. The
                traditional model prioritizes hiring and setup over early
                delivery.
              </motion.p>
            </div>

            <Row>
              {[
                {
                  icon: Zap,
                  color: "red",
                  title: "Speed Demands",
                  desc: "AI and digital programs demand unprecedented execution speed",
                  delay: 0.1,
                },
                {
                  icon: Shield,
                  color: "orange",
                  title: "Capability Ownership",
                  desc: "Enterprises need stronger internal control over core capabilities",
                  delay: 0.2,
                },
                {
                  icon: TrendingUp,
                  color: "amber",
                  title: "Vendor Dependency",
                  desc: "Rising reliance on external vendors limits agility and increases costs",
                  delay: 0.3,
                },
              ].map((item, index) => (
                <Col lg={4} md={6} className="mb-4" key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: item.delay }}
                    whileHover={{ y: -8 }}
                    className="problem-card"
                  >
                    {/* Decorative Shapes */}
                    <div className="shape-tr-circle"></div>
                    <div className="shape-tr-inner"></div>
                    <div className="shape-bl-circle"></div>
                    <div className="shape-diamond"></div>
                    <div className="line-top"></div>
                    <div className="line-bottom"></div>
                    <div className="circle-border-anim"></div>

                    {/* Icon */}
                    <div className="problem-icon-box">
                      <div className="icon-glow"></div>
                      <item.icon size={28} strokeWidth={2} />
                      <div className="mini-badge"></div>
                    </div>

                    {/* Content */}
                    <h3 className="problem-card-title">{item.title}</h3>
                    <p className="problem-card-text">{item.desc}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Why Enterprises Choose Comsense Section */}
        <section className="why-section bg-white">
          <div className="why-orb-1"></div>
          <div className="why-orb-2"></div>

          <Container className="position-relative z-1">
            <div className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* <div className="why-badge">
                  <div className="why-badge-dot"></div>
                  <span>WHY COMSENSE</span>
                </div> */}
                <h2 className="why-title">The Gap Between Setup and Impact</h2>
                <p className="why-subtitle">
                  Traditional GCC approaches often delay value creation instead
                  of accelerating it.
                </p>
              </motion.div>
            </div>

            <Row className="g-4">
              {[
                {
                  icon: Clock,
                  title: "Delayed Outcomes",
                  desc: "Traditional GCCs can take 6–12 months or more before delivering measurable business impact, delaying ROI and strategic initiatives.",
                },
                {
                  icon: DollarSign,
                  title: "High Upfront Investment",
                  desc: "Significant spending on infrastructure, technology, facilities, compliance, and operations can increase risk before value is realized.",
                },
                {
                  icon: Users,
                  title: "Talent Acquisition Challenges",
                  desc: "Attracting and retaining specialized talent across AI, analytics, cloud, cybersecurity, and enterprise applications remains one of the biggest barriers to scaling GCCs successfully.",
                },
                {
                  icon: Target,
                  title: "Strategic Misalignment",
                  desc: "GCC teams are often built before business objectives, governance frameworks, and success metrics are clearly defined, resulting in fragmented execution and reduced impact.",
                },
                {
                  icon: Settings,
                  title: "Scaling Complexity",
                  desc: "As GCCs grow, managing talent, processes, technology, and governance across multiple functions can increase operational complexity and reduce agility.",
                },
                {
                  icon: Shield,
                  title: "Operational Inefficiencies & Vendor Dependence",
                  desc: "Disconnected systems, siloed workflows, and reliance on multiple vendors can create inefficiencies, limit visibility, and reduce organizational control.",
                },
              ].map((card, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    className="h-100"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="why-card group">
                      <div className="why-card-halo"></div>
                      <div className="why-card-shine"></div>
                      <div className="why-accent-line"></div>
                      <div className="why-corner-l"></div>
                      <div className="why-corner-orb"></div>
                      <div className="why-bottom-glow"></div>

                      <div className="why-icon-box">
                        <div className="why-icon-glow"></div>
                        <card.icon size={32} strokeWidth={1.5} />
                      </div>

                      <h3 className="why-card-title">{card.title}</h3>
                      <p className="why-card-desc">{card.desc}</p>
                    </div>
                  </motion.div>
                </Col>
              ))}
              <Col xs={12} className="text-center mt-5">
                <p className="fw-bold  mb-0" style={{ color: "#028bc6" }}>
                  Result: Higher costs, slower execution, increased complexity,
                  and delayed business value.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        {/* OUR APPROACH Section */}
        <section className="model-section approach-model-section">
          {/* <div className="bg-orb bg-orb-1"></div>
          <div className="bg-orb bg-orb-2"></div> */}
          <Container>
            <div
              className="text-center mb-0 position-relative"
              style={{ zIndex: 2 }}
            >
              {/* <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="model-pill"
              >
                <span className="me-2"><CheckCircle2 size={14} /></span> Our Philosophy
              </motion.div> */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="model-title"
              >
                Our Approach
              </motion.h2>
              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="fw-bold mb-3"
                style={{ color: "#028bc6" }}
              >
                Start Small. Deliver Outcomes. Scale to a GCC.
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="model-subtitle mb-3"
              >
                Instead of starting with large teams and extended setup phases,
                we start with focused capability.
                {/* A pod of 10 to 25 specialists, aligned to a high-impact use case, delivers measurable outcomes within 60 to 90 days.
                This creates a validated foundation to scale into a full GCC. */}
              </motion.p>
            </div>
            {/* </Container>
        </section>


        <section className="model-section">
          <Container> */}
            <div
              className="text-center mb-5 position-relative"
              style={{ zIndex: 2 }}
            >
              {/* <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="model-pill"
              >
                <span className="me-2">
                  <CheckCircle2 size={14} />
                </span>{" "}
                Our Approach
              </motion.div> */}
              {/* <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="model-title"
              >
                How it Works
              </motion.h2> */}
              {/* <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="model-subtitle"
              >
                Pod → Scale → GCC
              </motion.p> */}
            </div>

            <Row className="position-relative">
              {[
                {
                  icon: Rocket,
                  title: "Step 1: Capability Pod",
                  time: "10–25 Team",
                  items: [
                    "Salesforce / Data / AI use case",
                    "Production-ready delivery",
                    "Outcomes within 60–90 days",
                  ],
                  delay: 0.15,
                },
                {
                  icon: TrendingUp,
                  title: "Step 2: Multi-Pod Expansion",
                  time: "Scaling Phase",
                  items: [
                    "Add analytics, AI, marketing ops",
                    "Build cross-functional capability",
                    "Strengthen governance",
                  ],
                  delay: 0.3,
                },
                {
                  icon: ArrowLeftRight,
                  title: "Step 3: Full GCC",
                  time: "Optional",
                  items: [
                    "Ownership transfer when mature",
                    "Complete documentation",
                    "Knowledge transition",
                    "Ongoing support",
                  ],
                  delay: 0.45,
                  isLast: true,
                },
              ].map((item, index) => (
                <Col
                  lg={4}
                  md={12}
                  className="mb-4 position-relative"
                  key={index}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -12 }}
                    viewport={{ once: true }}
                    transition={{ delay: item.delay, duration: 0.5 }}
                    className="model-card"
                  >
                    <div className="model-line-top"></div>
                    <div className="model-shape-tr"></div>
                    <div className="model-ripple-tr"></div>
                    <div className="model-shape-bl"></div>
                    <div className="model-shine-bottom"></div>

                    <div className="model-header">
                      <div className="model-icon-box">
                        <div className="model-icon-glow"></div>
                        <div className="model-icon-dot top-left"></div>
                        <div className="model-icon-dot bottom-right"></div>
                        <div className="model-icon-diamond"></div>
                        <item.icon size={32} strokeWidth={2} />
                      </div>
                      <div className="model-header-text">
                        <h3 className="model-card-title">{item.title}</h3>
                        <div className="model-time-pill">{item.time}</div>
                      </div>
                    </div>

                    <div className="d-flex flex-column gap-3">
                      {item.items.map((listItem, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.15 + i * 0.05 + 0.3 }}
                          className="model-list-item"
                        >
                          <div className="model-check-icon">
                            <CheckCircle2 size={16} strokeWidth={2.5} />
                          </div>
                          <div>{listItem}</div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Add button only to the last card */}
                    {/* {item.isLast && (
                      <div
                        className="mt-4 pt-2 position-relative"
                        style={{ zIndex: 2 }}
                      >
                        <Link
                          to="/contact"
                          className="btn-red w-100 text-center d-block text-decoration-none"
                        >
                          Contact Us
                        </Link>
                      </div>
                    )} */}
                  </motion.div>

                  {/* Arrow Connector between cards (desktop only) */}
                  {/* {!item.isLast && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 + 0.5 }}
                      className="arrow-connector-container d-none d-lg-block"
                    >
                      <div className="arrow-line"></div>
                      <div className="arrow-head"></div>
                    </motion.div>
                  )} */}
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* High-Value GCC Pods Section */}
        <section className="gcc-pods-section">
          <div className="gcc-pods-grid-bg"></div>

          {/* Animated Pulsing Orbs */}
          <div className="pod-orb pod-orb-tl"></div>
          <div className="pod-orb pod-orb-br"></div>

          <div className="section-glow-top"></div>

          <Container className="position-relative z-1">
            <div className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="pod-badge"
              >
                <span className="pod-badge-dot"></span> Specialized Capability
                Pods
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="gcc-pods-title"
              >
                High-Value GCC Pods
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="gcc-pods-subtitle"
              >
                Specialized capability centers designed to deliver measurable
                business outcomes
              </motion.p>
            </div>

            <Row>
              {[
                {
                  title: "Salesforce CoE",
                  icon: Cloud,
                  features: [
                    "Delivery pods (BA, Developer, Architect)",
                    "Data Cloud foundations",
                    "Integration & release governance",
                    "Managed run & enhancement factory",
                  ],
                  outcome: "Faster, higher-quality Salesforce delivery.",
                  delay: 0.1,
                },
                {
                  title: "AI & Data GCC",
                  icon: Database,
                  features: [
                    "Data engineering, analytics, ML & GenAI",
                    "Data quality & observability",
                    "MLOps & AI governance",
                    "AI enablement across the GCC",
                  ],
                  outcome: "Production-grade AI at scale.",
                  delay: 0.2,
                },
                {
                  title: "AI Product Factory",
                  icon: Rocket,
                  features: [
                    "AI product development & engineering",
                    "MLOps, deployment, product operations",
                    "Ready AI accelerators",
                    "Enterprise-grade engineering",
                  ],
                  outcome:
                    "AI products launched 2-3× faster with enterprise-grade engineering.",
                  delay: 0.3,
                },
              ].map((pod, index) => (
                <Col lg={4} md={6} className="mb-4" key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: pod.delay }}
                    className="h-100"
                  >
                    <div className="pod-dark-card group">
                      {/* Card Effects */}
                      <div className="pod-card-glow"></div>
                      <div className="pod-shine-overlay"></div>
                      <div className="pod-line-top"></div>
                      <div className="pod-bottom-glow"></div>

                      <div className="pod-card-dots">
                        <div className="pod-dot"></div>
                        <div className="pod-dot"></div>
                        <div className="pod-dot accent"></div>
                      </div>

                      <div className="pod-icon-wrapper">
                        <div className="pod-icon-glow"></div>
                        <pod.icon size={32} strokeWidth={1.5} />
                      </div>

                      <h3 className="pod-card-title">{pod.title}</h3>

                      <ul className="pod-feature-list">
                        {pod.features.map((feature, idx) => (
                          <li key={idx} className="pod-feature-item">
                            <span className="pod-bullet"></span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="pod-outcome-box">
                        <div className="pod-outcome-label">
                          <div className="pod-outcome-dot"></div>
                          OUTCOME
                        </div>
                        <p className="pod-outcome-text mb-0">{pod.outcome}</p>
                      </div>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Prime + Partner Model Section */}
        <section className="prime-partner-section">
          <div className="prime-orb-large"></div>
          <div className="prime-orb-small"></div>
          <Container className="position-relative z-1">
            <div className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* <div className="prime-badge">
                  <div className="prime-badge-dot"></div>
                  <span className="prime-badge-text">Proven Partnership</span>
                </div> */}
                <h2 className="prime-partner-title">Business Impact</h2>
                <p className="prime-partner-subtitle">What You Achieve</p>
              </motion.div>
            </div>

            <Row className="justify-content-center g-4">
              <Col lg={4} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="h-100"
                >
                  <div className="prime-partner-card group">
                    <div className="prime-card-halo"></div>
                    <div className="prime-shine-overlay"></div>
                    <div className="prime-top-line"></div>
                    <div className="prime-corner-shape"></div>
                    <div className="prime-bottom-glow"></div>

                    <div className="prime-card-header-row">
                      <div className="prime-icon-box red">
                        <div className="prime-icon-glow"></div>
                        <Zap size={32} strokeWidth={1.5} />
                      </div>
                      <div className="prime-header-text-col">
                        <h3 className="prime-card-brand">3x Faster</h3>
                        <div className="prime-role-label prime-role-comsense">
                          <div className="prime-role-line"></div>
                          <span className="prime-role-text">
                            Speed to Value
                          </span>
                        </div>
                      </div>
                    </div>

                    <ul className="prime-list">
                      {[
                        "Accelerated time to market compared to traditional GCC setupsOwns outcomes & governance",
                      ].map((item, i) => (
                        <li key={i} className="prime-list-item">
                          <div className="prime-bullet-container">
                            <div className="prime-bullet"></div>
                            <div className="prime-bullet-glow"></div>
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </Col>

              <Col lg={4} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="h-100"
                >
                  <div className="prime-partner-card group">
                    <div className="prime-card-halo"></div>
                    <div className="prime-shine-overlay"></div>
                    <div className="prime-top-line"></div>
                    <div className="prime-corner-shape"></div>
                    <div className="prime-bottom-glow"></div>

                    <div className="prime-card-header-row">
                      <div className="prime-icon-box dark-red">
                        <div className="prime-icon-glow"></div>
                        <DollarSign size={32} strokeWidth={1.5} />
                      </div>
                      <div className="prime-header-text-col">
                        <h3 className="prime-card-brand">30–50%</h3>
                        <div className="prime-role-label prime-role-srkay">
                          <div className="prime-role-line"></div>
                          <span className="prime-role-text">
                            Cost Efficiency
                          </span>
                        </div>
                      </div>
                    </div>

                    <ul className="prime-list">
                      {[
                        "Significant savings compared to onsite or traditional vendor models",
                      ].map((item, i) => (
                        <li key={i} className="prime-list-item">
                          <div className="prime-bullet-container">
                            <div className="prime-bullet"></div>
                            <div className="prime-bullet-glow"></div>
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </Col>

              <Col lg={4} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="h-100"
                >
                  <div className="prime-partner-card group">
                    <div className="prime-card-halo"></div>
                    <div className="prime-shine-overlay"></div>
                    <div className="prime-top-line"></div>
                    <div className="prime-corner-shape"></div>
                    <div className="prime-bottom-glow"></div>

                    <div className="prime-card-header-row">
                      <div className="prime-icon-box dark-red">
                        <div className="prime-icon-glow"></div>
                        <Target size={32} strokeWidth={1.5} />
                      </div>
                      <div className="prime-header-text-col">
                        <h3 className="prime-card-brand">90 Days</h3>
                        <div className="prime-role-label prime-role-srkay">
                          <div className="prime-role-line"></div>
                          <span className="prime-role-text">
                            Rapid Outcomes
                          </span>
                        </div>
                      </div>
                    </div>

                    <ul className="prime-list">
                      {[
                        "Measurable business impact delivered within the first quarter",
                      ].map((item, i) => (
                        <li key={i} className="prime-list-item">
                          <div className="prime-bullet-container">
                            <div className="prime-bullet"></div>
                            <div className="prime-bullet-glow"></div>
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </Col>

              <Col lg={4} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="h-100"
                >
                  <div className="prime-partner-card group">
                    <div className="prime-card-halo"></div>
                    <div className="prime-shine-overlay"></div>
                    <div className="prime-top-line"></div>
                    <div className="prime-corner-shape"></div>
                    <div className="prime-bottom-glow"></div>

                    <div className="prime-card-header-row">
                      <div className="prime-icon-box dark-red">
                        <div className="prime-icon-glow"></div>
                        <Shield size={32} strokeWidth={1.5} />
                      </div>
                      <div className="prime-header-text-col">
                        <h3 className="prime-card-brand">100%</h3>
                        <div className="prime-role-label prime-role-srkay">
                          <div className="prime-role-line"></div>
                          <span className="prime-role-text">Control</span>
                        </div>
                      </div>
                    </div>

                    <ul className="prime-list">
                      {[
                        "Reduced vendor dependency with strong internal ownership",
                      ].map((item, i) => (
                        <li key={i} className="prime-list-item">
                          <div className="prime-bullet-container">
                            <div className="prime-bullet"></div>
                            <div className="prime-bullet-glow"></div>
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </Col>

              <Col lg={4} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="h-100"
                >
                  <div className="prime-partner-card group">
                    <div className="prime-card-halo"></div>
                    <div className="prime-shine-overlay"></div>
                    <div className="prime-top-line"></div>
                    <div className="prime-corner-shape"></div>
                    <div className="prime-bottom-glow"></div>

                    <div className="prime-card-header-row">
                      <div className="prime-icon-box dark-red">
                        <div className="prime-icon-glow"></div>
                        <Target size={32} strokeWidth={1.5} />
                      </div>
                      <div className="prime-header-text-col">
                        <h3 className="prime-card-brand">Expertise</h3>
                        <div className="prime-role-label prime-role-srkay">
                          <div className="prime-role-line"></div>
                          <span className="prime-role-text">
                            In-House Ownership
                          </span>
                        </div>
                      </div>
                    </div>

                    <ul className="prime-list">
                      {["Build and retain capabilities internally"].map(
                        (item, i) => (
                          <li key={i} className="prime-list-item">
                            <div className="prime-bullet-container">
                              <div className="prime-bullet"></div>
                              <div className="prime-bullet-glow"></div>
                            </div>
                            {item}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                </motion.div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* WHAT WE BUILD Section */}
        <section className="proven-outcomes-section">
          <div className="outcome-orb-left"></div>
          <div className="outcome-orb-right"></div>
          <Container className="position-relative z-1">
            <div className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* <div className="prime-badge">
                  <div className="prime-badge-dot"></div>
                  <span className="prime-badge-text">Core Expertise</span>
                </div> */}
                <h2 className="outcome-title">What we Build</h2>
                <p className="prime-partner-subtitle mb-3">
                  Customer & Data Capability Centers
                </p>
                <p className="" style={{ fontWeight: 600, color: "#028bc6" }}>
                  We specialize in building high-impact capabilities across:
                </p>
              </motion.div>
            </div>

            <Row className="justify-content-center g-4">
              {[
                {
                  title: "AI & Intelligence",
                  items: [
                    "AI / GenAI",
                    "Decision Intelligence",
                    "Predictive Analytics",
                  ],
                  icon: Brain,
                },
                {
                  title: "Salesforce Ecosystem",
                  items: [
                    "Sales & Service Cloud",
                    "Marketing Cloud",
                    "Data Cloud",
                  ],
                  icon: Cloud,
                },
                {
                  title: "Data Foundations",
                  items: [
                    "CDPs (Customer Data Platforms)",
                    "Data Engineering",
                    "Advanced Analytics",
                  ],
                  icon: Database,
                },
                {
                  title: "Marketing & Growth",
                  items: [
                    "Marketing Automation",
                    "Personalization Engine",
                    "Revenue Ops",
                  ],
                  icon: Zap,
                },
              ].map((build, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="h-100"
                  >
                    <div className="outcome-card group h-100">
                      {/* Decorative Shapes */}
                      <div className="shape-tr-circle"></div>
                      <div className="shape-tr-inner"></div>
                      <div className="shape-bl-circle"></div>
                      <div className="shape-diamond"></div>
                      <div className="line-top"></div>
                      <div className="line-bottom"></div>
                      <div className="circle-border-anim"></div>

                      <div className="outcome-icon-box red mb-3">
                        <div className="icon-glow"></div>
                        <build.icon size={28} />
                        <div className="mini-badge"></div>
                      </div>
                      <h3 className="fs-5 fw-bold mb-3">{build.title}</h3>
                      <ul className="list-unstyled">
                        {build.items.map((item, i) => (
                          <li key={i} className="small mb-2 opacity-75">
                            <CheckCircle2
                              size={14}
                              className="me-2"
                              style={{ color: "#067bb1" }}
                            />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </Col>
              ))}
              <Col xs={12} className="text-center mt-5">
                <p className="fw-bold fs-4 mb-0">
                  <span className="gcc-highlight-marker">
                    We architect not just Teams but capability that drives
                    Revenue and Efficiency.
                  </span>
                </p>
              </Col>
            </Row>
          </Container>
        </section>
        {/* WHY COMSENSE Section */}
        <section className="why-section bg-white">
          <div className="why-orb-1"></div>
          <div className="why-orb-2"></div>

          <Container className="position-relative z-1">
            <div className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="why-badge">
                  <div className="why-badge-dot"></div>
                  <span>WHY COMSENSE</span>
                </div>
                <h2 className="why-title">Outcome-Led GCC Builder</h2>
                <p className="why-subtitle">
                  We don't just build infrastructure; we build the capability
                  that makes GCC valuable.
                </p>
              </motion.div>
            </div>

            <Row className="g-4 justify-content-center">
              {[
                {
                  icon: Target,
                  title: "Outcome-first",
                  desc: "We prioritize business impact over hiring-first approaches, ensuring early delivery.",
                },
                {
                  icon: Zap,
                  title: "Launch in 90 days",
                  desc: "Get your production-ready capability team operational in just one quarter.",
                },
                {
                  icon: TrendingUp,
                  title: "Designed for scale",
                  desc: "Built from day one to grow with your enterprise needs and digital maturity.",
                },
                {
                  icon: Brain,
                  title: "Deep Expertise",
                  desc: "Specialized knowledge across Customer, Data, and AI ecosystems.",
                },
              ].map((card, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div
                    className="h-100"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="why-card group">
                      <div className="why-card-halo"></div>
                      <div className="why-card-shine"></div>
                      <div className="why-accent-line"></div>
                      <div className="why-corner-l"></div>
                      <div className="why-corner-orb"></div>
                      <div className="why-bottom-glow"></div>
                      <div className="why-icon-box">
                        <div className="why-icon-glow"></div>
                        <card.icon size={32} strokeWidth={1.5} />
                      </div>
                      <h3 className="why-card-title">{card.title}</h3>
                      <p className="why-card-desc">{card.desc}</p>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
            <div className="text-center mt-5">
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="fw-bold fs-4"
              >
                <span className="gcc-highlight-marker">
                  Others build GCC infrastructure. We build the capability that
                  makes GCC valuable.
                </span>
              </motion.p>
            </div>
          </Container>
        </section>

        {/* EXECUTION Section */}
        <section className="launch-sprint-section pt-0">
          <div className="launch-bg-orb-large"></div>
          <div className="launch-bg-orb-small"></div>
          <Container className="position-relative z-1">
            <motion.div
              className="launch-container"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="launch-top-accent"></div>
              <div className="launch-corner-tr"></div>
              <div className="launch-corner-bl"></div>
              <div className="launch-grid-bg"></div>
              <div className="launch-orb-1"></div>
              <div className="launch-orb-2"></div>

              <div className="launch-content">
                <div className="text-center mb-5">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="launch-badge"
                  >
                    <div className="launch-badge-dot"></div>
                    <span>EXECUTION</span>
                  </motion.div>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="launch-title"
                  >
                    Execution Backed by a Proven GCC Engine
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="launch-subtitle opacity-75"
                  >
                    In partnership with SRKay Consulting Group
                  </motion.p>
                </div>

                <Row className="g-4">
                  <Col lg={5}>
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="launch-card h-100"
                    >
                      <div className="launch-card-halo"></div>
                      <div className="launch-card-shine"></div>
                      <div className="d-block d-md-flex items-center">
                        <div className="launch-icon-wrapper mb-4">
                          <div className="launch-icon-glow"></div>
                          <div className="launch-icon-box">
                            <Building2 size={40} />
                          </div>
                        </div>
                        <div className="ms-0 ms-md-4">
                          <h3 className="launch-days-number">12+</h3>
                          <p className="launch-days-label">
                            GCCs Enabled Globally
                          </p>
                        </div>
                      </div>

                      <div className="launch-divider"></div>
                      <p className="launch-days-desc">
                        Proven Execution Engine
                      </p>
                      <p className="launch-foundation-text mt-3 opacity-75">
                        Our partnership combines deep capability with a
                        rock-solid operational foundation.
                      </p>
                      <div className="launch-card-bottom-glow"></div>
                    </motion.div>
                  </Col>

                  <Col lg={7}>
                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 }}
                      className="launch-card h-100"
                    >
                      <div className="launch-card-halo"></div>
                      <div className="launch-card-shine"></div>
                      <h3 className="launch-deliverables-title mb-4">
                        A Fully Integrated GCC Model
                      </h3>
                      <ul className="launch-list">
                        {[
                          {
                            icon: Globe,
                            text: "150+ global clients across multiple domains",
                          },
                          {
                            icon: Repeat,
                            text: "Proven Build-Operate-Transfer (BOT) model for seamless ownership",
                          },
                          {
                            icon: ShieldCheck,
                            text: "End-to-end compliance, hiring, and governance frameworks",
                          },
                          {
                            icon: Zap,
                            text: "Accelerated setup minimizing risk and maximizing speed to value",
                          },
                        ].map((item, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.5 + i * 0.1 }}
                            className="launch-list-item"
                          >
                            <div className="launch-check-icon">
                              <div className="launch-check-glow"></div>
                              <item.icon size={20} />
                            </div>
                            <span className="launch-list-text">
                              {item.text}
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                      <div className="launch-card-bottom-glow"></div>
                    </motion.div>
                  </Col>
                </Row>
              </div>
            </motion.div>
          </Container>
        </section>

        {/* Why Enterprises Choose Comsense Section */}
        <section className="why-section">
          <div className="why-orb-1"></div>
          <div className="why-orb-2"></div>

          <Container className="position-relative z-1">
            <div className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="why-badge">
                  <div className="why-badge-dot"></div>
                  <span>Proof</span>
                </div>
                <h2 className="why-title">Example Impact</h2>
              </motion.div>
            </div>

            <Row className="g-4">
              {[
                {
                  icon: Factory,
                  title: "Manufacturing",
                  desc: (
                    <>
                      Unified <strong>60+ ERP</strong> systems across{" "}
                      <strong>50+ countries</strong> into SAP S/4HANA. Delivered{" "}
                      <strong>40% cost reduction</strong> and{" "}
                      <strong>2x</strong> improved{" "}
                      <strong>user experience.</strong>
                    </>
                  ),
                },
                {
                  icon: HeartPulse,
                  title: "Healthcare",
                  desc: (
                    <>
                      Set up India-based AI/ML team unlocking{" "}
                      <strong>60% cost advantage.</strong> Accelerated
                      innovation with <strong>20% faster</strong>{" "}
                      time-to-market.
                    </>
                  ),
                },
                {
                  icon: Shirt,
                  title: "Retail Sportswear",
                  desc: (
                    <>
                      Centralized marketing operations across{" "}
                      <strong>16 markets.</strong> Reduced{" "}
                      <strong>costs</strong> by <strong>25%</strong> and
                      increased <strong>engagement</strong> by{" "}
                      <strong>22%.</strong>{" "}
                    </>
                  ),
                },
              ].map((card, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    className="h-100"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="why-card group">
                      <div className="why-card-halo"></div>
                      <div className="why-card-shine"></div>
                      <div className="why-accent-line"></div>
                      <div className="why-corner-l"></div>
                      <div className="why-corner-orb"></div>
                      <div className="why-bottom-glow"></div>

                      <div className="why-icon-box">
                        <div className="why-icon-glow"></div>
                        <card.icon size={32} strokeWidth={1.5} />
                      </div>

                      <h3 className="why-card-title">{card.title}</h3>
                      <p className="why-card-desc">{card.desc}</p>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Trusted By Industry Leaders Section */}
        {/* <section className="trusted-section">
                    <div className="trusted-orb trusted-orb-1"></div>
                    <div className="trusted-orb trusted-orb-2"></div>

                    <Container className="position-relative z-1">
                        <div className="text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="trusted-badge">
                                    <div className="trusted-badge-dot"></div>
                                    <span>TRUSTED BY INDUSTRY LEADERS</span>
                                </div>
                                <h2 className="trusted-title">Powering Global Brands</h2>
                                <p className="trusted-subtitle">
                                </p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: 0.2 }}
                                className="brand-container"
                            >
                                <div className="brand-accent-line"></div>
                                <div className="brand-corner-tr"></div>
                                <div className="brand-corner-bl"></div>
                                <div className="brand-shine"></div>

                                <div className="brand-logos-row">
                                    <img src={AdidasLogo} alt="Adidas" className="brand-logo-item" />
                                    <img src={ArthrexLogo} alt="Arthrex" className="brand-logo-item" />
                                    <img src={McDonaldsLogo} alt="McDonald's" className="brand-logo-item" />
                                    <img src={RoyalEnfieldLogo} alt="Royal Enfield" className="brand-logo-item" />
                                    <img src={SouthwestLogo} alt="Southwest" className="brand-logo-item" />
                                </div>
                            </motion.div>


                        </div>
                    </Container>
                </section> */}

        {/* Connect With Us Section */}
        {/* <section className="connect-section">
                    <div className="connect-orb-1"></div>
                    <div className="connect-orb-2"></div>

                    <Container className="position-relative z-1">
                        <div className="text-center mb-5">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="connect-badge">
                                    <div className="connect-badge-dot"></div>
                                    <span>GET IN TOUCH</span>
                                </div>
                                <h2 className="connect-title">Connect With Us</h2>
                                <p className="connect-subtitle">
                                    Ready to transform your GCC strategy? Let's talk.
                                </p>
                            </motion.div>
                        </div>

                        <Row className="justify-content-center g-4 mb-5">
                            <Col md={6} lg={5}>
                                <motion.div
                                    initial={{ opacity: 0, x: -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="h-100"
                                >
                                    <a href="mailto:vineet@comsenseconsulting.com" className="text-decoration-none">
                                        <div className="connect-card">
                                            <div className="connect-card-halo"></div>
                                            <div className="connect-card-shine"></div>
                                            <div className="connect-bottom-glow"></div>

                                            <div className="connect-icon-box">
                                                <Mail size={32} />
                                            </div>

                                            <div className="connect-card-content">
                                                <span className="connect-label">Email</span>
                                                <span className="connect-value">vineet@comsenseconsulting.com</span>
                                            </div>
                                        </div>
                                    </a>
                                </motion.div>
                            </Col>

                            <Col md={6} lg={5}>
                                <motion.div
                                    initial={{ opacity: 0, x: 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.3 }}
                                    className="h-100"
                                >
                                    <a href="https://www.comsensetechnologies.com" target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                        <div className="connect-card">
                                            <div className="connect-card-halo"></div>
                                            <div className="connect-card-shine"></div>
                                            <div className="connect-bottom-glow"></div>

                                            <div className="connect-icon-box">
                                                <Globe size={32} />
                                            </div>

                                            <div className="connect-card-content">
                                                <span className="connect-label">Website</span>
                                                <span className="connect-value">www.comsensetechnologies.com</span>
                                            </div>
                                        </div>
                                    </a>
                                </motion.div>
                            </Col>
                        </Row>

                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            <Row className="justify-content-center">
                                <Col lg={10}>
                                    <div className="connect-cta-card text-center">
                                        <div className="connect-cta-halo"></div>
                                        <div className="connect-cta-accent"></div>
                                        <div className="connect-corner-tr"></div>
                                        <div className="connect-corner-bl"></div>

                                        <h3 className="connect-cta-title">Get Started Today</h3>
                                        <p className="connect-cta-desc">
                                            Schedule a consultation to discuss how Comsense can help you build a high-impact GCC in 90 days with zero upfront CapEx.
                                        </p>

                                        <div>
                                            <Link to="/contact" className="connect-btn-primary">
                                                Schedule Consultation
                                            </Link>
                                            <Link to="/about" className="connect-btn-secondary">
                                                Learn More
                                            </Link>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </motion.div>
                    </Container>
                </section> */}
      </div>
      <div className="gcc-outfit">
        {/* WHEN THIS IS RIGHT FOR YOU Section */}
        <section className="proven-outcomes-section bg-white">
          <div className="outcome-orb-left"></div>
          <div className="outcome-orb-right"></div>
          <Container className="position-relative z-1">
            <div className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* <div className="why-badge">
                  <div className="why-badge-dot"></div>
                  <span>When This Is Right For You</span>
                </div> */}
                <h2 className="outcome-title">When This Is Right For You</h2>
              </motion.div>
            </div>
            <Row className="g-4">
              {[
                {
                  icon: LinkIcon,
                  text: "You are over-dependent on SI vendors",
                  title: "Vendor Dependency",
                },
                {
                  icon: Clock,
                  text: "Your AI, marketing, or data initiatives are slow",
                  title: "Execution Speed",
                },
                {
                  icon: UserCheck,
                  text: "You want stronger internal capability ownership",
                  title: "Internal Ownership",
                },
                {
                  icon: BarChart,
                  text: "You are scaling digital or AI-led transformation",
                  title: "Scaling Transformation",
                },
              ].map((item, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="h-100"
                  >
                    <div className="outcome-card group">
                      <div className="outcome-card-halo"></div>
                      <div className="outcome-shine-overlay"></div>
                      <div className="outcome-top-line"></div>
                      <div className="outcome-corner-shape"></div>
                      <div className="outcome-bottom-glow"></div>

                      <div className="outcome-header-row mb-4">
                        <div className="outcome-icon-box red">
                          <div className="outcome-icon-glow"></div>
                          <item.icon size={28} strokeWidth={1.5} />
                        </div>
                        <div className="outcome-header-text">
                          <h3 className="outcome-card-title fs-5">
                            {item.title}
                          </h3>
                        </div>
                      </div>

                      <div className="outcome-metrics">
                        <div
                          className="outcome-metric-row"
                          style={{ padding: "12px" }}
                        >
                          <p className="small mb-0 opacity-75">{item.text}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Final CTA Section */}
        <section className="launch-sprint-section pt-0">
          <Container>
            <motion.div
              className="launch-container pt-0 pb-0 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="launch-bg-orb-large"></div>
              <div className="launch-grid-bg"></div>
              <div className="launch-content py-5">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="launch-title mb-3"
                >
                  Start Your GCC, Without the Risk
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="launch-subtitle mb-4"
                >
                  Launch your first capability team in 90 days. Scale into a
                  full GCC with proven outcomes.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <button
                    type="button"
                    className="btn-red text-decoration-none d-inline-block"
                    onClick={openDataReadinessModal}
                  >
                    Get Your GCC Blueprint
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </Container>
        </section>

        <section
          className="launch-sprint-section testimonials-section pt-0"
          id="testimonials"
        >
          <div className="launch-bg-orb-large"></div>
          <div className="launch-bg-orb-small"></div>
          <Container className="position-relative z-1">
            <motion.div
              className="launch-containers"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="launch-content">
                <div className="text-center mb-5">
                  {/* <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="launch-badge"
                  >
                    TESTIMONIALS
                  </motion.div> */}
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="launch-title"
                  >
                    Testimonials
                  </motion.h2>
                </div>

                <Row className="g-4">
                  <Col lg={10} className="mx-auto">
                    <Carousel
                      controls
                      indicators
                      interval={7000}
                      pause={false}
                      fade
                      className="testimonial-carousel"
                    >
                      {[
                        {
                          text: "The BOT-led operating model enabled us to standardize critical business processes across more than 50 countries while accelerating a large-scale ERP transformation. The team's ability to establish scalable delivery capabilities, drive operational efficiencies, and support global execution exceeded our expectations and created lasting business value.",
                          author:
                            "— IT Director, Fortune 500 Chemical Enterprise",
                        },
                        {
                          text: "The Virtual Captive model gave us the confidence to scale from a pilot initiative to a 100+ member Testing Center of Excellence. The team's focus on governance, quality, and talent development helped us build a sustainable global capability that continues to drive process optimization and delivery excellence.",
                          author:
                            "— VP Operations, Leading North American Airline",
                        },
                        {
                          text: "The team helped us build a scalable data and AI capability that significantly improved customer retention, increased service revenue, and enhanced customer experience outcomes. Their ability to understand our business challenges and deliver contextualized, data-driven solutions strengthened our long-term digital and analytics capabilities.",
                          author:
                            "— Head of Customer Experience, Global Automotive Manufacturer",
                        },
                        {
                          text: "The team became a strategic extension of our organization, helping us scale CRM, e-commerce, and digital transformation initiatives through deep expertise in AI, data, and advanced analytics. Their ability to combine specialized talent with business understanding enabled us to deliver highly personalized customer experiences and drive measurable outcomes across multiple channels.",
                          author:
                            "— Digital Transformation Leader, Global Retail Brand",
                        },
                      ].map((t, i) => (
                        <Carousel.Item key={i}>
                          <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.15 + i * 0.03 }}
                            className="testimonial-card launch-card mx-auto"
                            style={{ maxWidth: 900 }}
                          >
                            <div
                              className="mb-3"
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "12px",
                              }}
                            >
                              <Quote
                                size={32}
                                className="text-opacity-15"
                                style={{
                                  color: "rgba(6,123,177,0.15)",
                                  flexShrink: 0,
                                  marginTop: "-4px",
                                  transform: "rotate(180deg)",
                                }}
                              />
                              <blockquote style={{ margin: 0 }}>
                                {t.text}
                              </blockquote>
                            </div>
                            <div className="text-end text-muted">
                              {t.author}
                            </div>
                          </motion.div>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  </Col>
                </Row>
              </div>
            </motion.div>
          </Container>
        </section>

        <section className="launch-sprint-section faq-section pt-0" id="FAQs">
          <div className="launch-bg-orb-large"></div>
          <div className="launch-bg-orb-small"></div>
          <Container className="position-relative z-1">
            <motion.div
              className="launch-containers"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* <div className="launch-top-accent"></div>
              <div className="launch-corner-tr"></div>
              <div className="launch-corner-bl"></div>
              <div className="launch-grid-bg"></div>
              <div className="launch-orb-1"></div>
              <div className="launch-orb-2"></div> */}

              <div className="launch-content">
                <div className="text-center mb-5">
                  {/* <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="launch-badge"
                  >
                    FAQs
                  </motion.div> */}
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="launch-title"
                  >
                    FAQs
                  </motion.h2>
                </div>

                <Row className="justify-content-center">
                  <Col lg={10}>
                    <Accordion
                      activeKey={activeKey}
                      onSelect={(key) => setActiveKey(key)}
                      className="faq-accordion modern-faq-accordion"
                    >
                      {[
                        {
                          question: "What is a Global Capability Center (GCC)?",
                          answer:
                            "A Global Capability Center (GCC) is a dedicated offshore or nearshore unit that delivers business-critical capabilities such as technology, analytics, customer experience, engineering, and digital transformation. Modern GCCs have evolved beyond support functions and now serve as strategic hubs for innovation, AI adoption, and business growth.",
                        },
                        {
                          question:
                            "Why are companies investing in Global Capability Centers?",
                          answer:
                            "Organizations invest in GCCs to access specialized talent, improve operational efficiency, accelerate innovation, and scale global capabilities. Increasingly, GCCs are also being leveraged to drive AI initiatives, analytics programs, customer experience transformation, and enterprise-wide digital modernization.",
                        },
                        {
                          question:
                            "How are modern GCCs different from traditional offshore centers?",
                          answer:
                            "Traditional offshore centers primarily focused on cost optimization and process execution. Modern GCCs are designed to create strategic business value by driving innovation, data intelligence, AI adoption, automation, and digital transformation. Their success is measured by business outcomes rather than on an operational scale alone.",
                        },
                        {
                          question: "What is an AI-first GCC?",
                          answer:
                            "An AI-first GCC integrates artificial intelligence, automation, analytics, and data intelligence into its operating model from the outset. These centers help organizations improve decision-making, automate workflows, enhance customer experiences, and accelerate innovation while building future-ready enterprise capabilities.",
                        },
                        {
                          question:
                            "Why is India a preferred destination for GCCs?",
                          answer:
                            "India offers access to a large pool of highly skilled professionals across AI, analytics, software engineering, cloud technologies, and enterprise applications. Combined with a mature technology ecosystem, strong infrastructure, and scalability, India remains one of the world's leading destinations for establishing and expanding GCCs.",
                        },
                        {
                          question:
                            "How does Comsense Technologies help organizations establish GCCs?",
                          answer:
                            "Comsense Technologies helps enterprises build and scale AI-first Global Capability Centers through a combination of strategy, talent, technology, governance, and operational support. Our approach focuses on accelerating time-to-value while enabling organizations to build future-ready capabilities in AI, analytics, customer experience, Salesforce, and digital transformation.",
                        },
                        {
                          question:
                            "What makes Comsense's GCC approach different?",
                          answer:
                            "Unlike traditional GCC models that focus primarily on headcount and cost optimization, Comsense emphasizes business outcomes, AI readiness, operational agility, and scalable capability building. We help organizations create intelligent capability ecosystems that support innovation, data-driven decision-making, and long-term enterprise growth.",
                        },
                        {
                          question:
                            "How quickly can organizations establish a GCC with Comsense?",
                          answer:
                            "The timeline varies based on business objectives, operating model, and capability requirements. However, Comsense's structured GCC framework helps organizations accelerate setup, reduce operational complexity, and achieve faster business value compared to conventional GCC establishment approaches.",
                        },
                        {
                          question:
                            "What is the future of Global Capability Centers?",
                          answer:
                            "The future of GCCs lies in AI-first, intelligence-driven operating models that combine technology, analytics, automation, and specialized talent. As enterprises continue to prioritize innovation and digital transformation, GCCs are expected to evolve into strategic growth engines that drive business value, accelerate AI adoption, and enable global scalability.",
                        },
                      ].map((faq, index) => (
                        <Accordion.Item
                          eventKey={`${index}`}
                          key={index}
                          className="faq-card-item"
                        >
                          <Accordion.Header>{faq.question}</Accordion.Header>
                          <Accordion.Body>{faq.answer}</Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </Col>
                </Row>
              </div>
            </motion.div>
          </Container>
        </section>
        <Modal
          show={showDataReadinessModal}
          size="xl"
          onHide={closeDataReadinessModal}
          aria-labelledby="contained-modal-title-vcenter"
          centered
          style={{ overflow: "hidden" }}
        >
          <style>
            {`.btn-close {
            position: absolute;
            top: 1rem;
            right: 1rem;
            z-index: 2;
          }
          .modal-content{overflow:hidden !important;}
          `}
          </style>
          <Modal.Header className="p-0" closeButton></Modal.Header>
          <Modal.Body className="p-0">
            <DataReadinessModalContent
              key={showDataReadinessModal ? "open" : "closed"}
            />
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default GlobalCapabilityCenter;
